// src/gestion-vente/GestionVente.tsx
import ManualInvoiceNumberField from "../components/invoices/ManualInvoiceNumberField";
import { useManualInvoiceNumber } from "../hooks/useInvoiceNumbering";
import { ensureSubscriptionActionAllowed } from '../utils/subscriptionActionGuard';
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
} from "react";
import {
  Layout,
  Space,
  Button,
  Badge,
  Dropdown,
  Tooltip,
  Input,
  Select,
  DatePicker,
  Table,
  Modal,
  Form,
  InputNumber,
  Spin,
  message,
  Row,
  Col,
  Tag,
  Tabs,
  AutoComplete,
  Card,
  Checkbox,
  Alert,
  Collapse,
  Empty,
} from "antd";
import type { TableProps, Breakpoint } from "antd"; // Corrected Breakpoint import
import type { RangePickerProps } from "antd/es/date-picker";
import {
  PlusOutlined,
  SearchOutlined,
  QrcodeOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  DownOutlined,
  StopOutlined,
  RollbackOutlined,
  GlobalOutlined,
  ShopOutlined,
  BarChartOutlined,
  FileTextOutlined,
  PictureOutlined,
  SaveOutlined,
  ShoppingCartOutlined,
  CheckOutlined,
  WalletOutlined,
  ClockCircleOutlined,
  PieChartOutlined,
  FilterOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import BarcodeScanner from "./BarcodeScanner";
import OrderProductSearch from "../components/orders/OrderProductSearch";
import useOrderFormOptions, { choiceItemId, fetchOrderFormOptions } from "../components/orders/useOrderFormOptions";
import BusinessResourceNotice from "../components/BusinessResourceNotice";
import useBusinessMutationInvalidation from "../hooks/useBusinessMutationInvalidation";
import { saleFormData } from "../components/orders/orderFormData";
import { INVOICE_SUBJECT_MAX_LENGTH, isInvoiceSubjectEnabled, normalizeInvoiceSubject } from "../utils/invoiceSubject";
import usePaymentFormChoices from "../gestion-payments/usePaymentFormChoices";
import { useTaxes } from "./useTaxes";
import useSaleDetailLink from "./useSaleDetailLink";
import { useSelection } from "../SelectionContext";
import { debounce } from "lodash";
import VenteDetailModal from "./components/VenteDetailModal";
import { InvoicePDF } from "./components/index";
import QuickClientModal from "./components/QuickClientModal";
import API_CONFIG from "../config/api.js";
import { generateInvoicePDF } from "./components/InvoicePDFGenerator";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { generateInvoiceHTML } from "./components/InvoiceHTMLTemplate";
import { downloadSalesDeliveryPdf } from "./deliveryApi";
// Import useAuth hook
import { useAuth } from "../context/AuthContext";
import { fetchWithCache, buildCacheKey } from "../utils/offlineCache";
import {
  enqueueRequest,
  buildTempId,
  getQueueEntriesForCurrentContext,
  isOffline,
} from "../utils/offlineQueue";
import { playBusinessAudioAlert } from "../utils/restaurantAudio";
import BusinessAudioToggle from "../components/BusinessAudioToggle";
import TicketCaisse from "../gestion-pos/TicketCaisse";
import RowActionsMenu from "../components/RowActionsMenu";
import { getSalesReturnDocumentDisabledReason, useSalesReturnDocument } from "./components/SalesReturnDocumentAction";
import OrderInitialPaymentFields, {
  buildInitialPaymentPayload,
} from "../components/orders/OrderInitialPaymentFields";
import {
  buildCommercialEntitySearchText,
  getCommercialEntityDisplayName,
  matchesCommercialEntitySearch,
} from "../utils/entityUtils";
import StockShortageActionsModal from "../components/StockShortageActionsModal";
import {
  buildMissingStockItems,
  buildSalesProductionReplenishmentNavigation,
  buildSalesProductionReplenishmentRequest,
  buildStockReplenishmentRequest,
  splitStockShortageItems,
} from "../utils/stockReplenishment";
import {
  deleteSalesReplenishmentDraft,
  finalizeSalesReplenishmentDraft,
  loadSalesReplenishmentDraft,
  mergeManualSaleDraftWithShortage,
  reconcileManualSaleDraftItems,
  saveSalesReplenishmentDraft,
} from "../utils/salesReplenishmentDraft";
import { getSelectedProductionTerminology } from "../GestionProductions/productionTerminology";
import { normalizeImageUrl } from "../utils/imageUrlHelper.js";
import "./GestionVenteForm.css";
import "./GestionVentePage.css";

const { Option } = Select;
const { RangePicker } = DatePicker;
const QUANTITY_DECIMAL_PLACES = 5;
const MIN_SALE_QUANTITY = 0.00001;
const PURCHASE_VIEW_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Achats.Achat.view";
const PURCHASE_CREATE_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Achats.Achat.create";
const STOCK_ADJUST_VIEW_PERMISSION =
  "Gestion Commerciale.Stock.GestionStock.Adjust.view";
const STOCK_ADJUST_CREATE_PERMISSION =
  "Gestion Commerciale.Stock.GestionStock.Adjust.create";
const PRODUCTION_MANAGE_VIEW_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Production.Gerer.view";
const PRODUCTION_MANAGE_CREATE_PERMISSION =
  "Gestion Commerciale.Approvisionnement.Production.Gerer.create";

type SalesChannel = "all" | "manual" | "pos" | "ecommerce";

const SALES_CHANNEL_OPTIONS: Array<{
  value: SalesChannel;
  label: string;
}> = [
  { value: "all", label: "Tous les canaux" },
  { value: "ecommerce", label: "Boutique en ligne" },
  { value: "pos", label: "Point de vente" },
  { value: "manual", label: "Vente manuelle" },
];

const isEcommerceSale = (sale: any): boolean =>
  sale?.record_source === "ecommerce" || sale?.sales_channel === "ecommerce";

const getSalesChannelDisplay = (channel: string | null | undefined) => {
  if (channel === "ecommerce") {
    return { label: "Boutique en ligne", color: "blue", icon: <GlobalOutlined /> };
  }
  if (channel === "pos") {
    return { label: "Point de vente", color: "purple", icon: <ShopOutlined /> };
  }
  return { label: "Vente manuelle", color: "default", icon: <EditOutlined /> };
};

const getSalesDateRangePresets = (): NonNullable<
  RangePickerProps["presets"]
> => [
  { label: "Aujourd'hui", value: [dayjs(), dayjs()] },
  { label: "7 derniers jours", value: [dayjs().subtract(6, "days"), dayjs()] },
  {
    label: "30 derniers jours",
    value: [dayjs().subtract(29, "days"), dayjs()],
  },
  { label: "Ce mois", value: [dayjs().startOf("month"), dayjs().endOf("month")] },
  {
    label: "Mois dernier",
    value: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
  {
    label: "Cette année",
    value: [dayjs().startOf("year"), dayjs().endOf("year")],
  },
];

// Remplaçons la définition du type AuthContextType par une version plus complète
interface AuthUser {
  id?: number | string;
  permissions?: string[];
  name?: string;
  email?: string;
  // Autres propriétés possibles...
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  isLoading?: boolean;
  login: (token: string, userData: any) => void;
  logout: () => void;
  hasPermission?: (permission: string) => boolean;
}

// Améliorons la fonction checkPermission pour qu'elle soit plus robuste
const checkPermission = (auth: any, permission: string): boolean => {
  // Vérifier si l'objet auth existe
  if (!auth) {
    console.warn(
      `Permission check failed: Auth context not available for: ${permission}`,
    );
    return false;
  }

  // Ajouter des logs pour le débogage (à commenter en production)
  // console.debug(`Checking permission: ${permission}`);
  // console.debug(
  //   `Auth object structure:`,
  //   Object.keys(auth).length > 0 ? Object.keys(auth) : "Empty auth object"
  // );

  // Méthode 1: hasPermission est une méthode directement sur l'objet auth
  if (typeof auth.hasPermission === "function") {
    // console.debug(`Using auth.hasPermission method`);
    return auth.hasPermission(permission);
  }

  // Méthode 2: Si auth.user existe, vérifier s'il a une méthode hasPermission
  if (auth.user && typeof auth.user.hasPermission === "function") {
    // console.debug(`Using auth.user.hasPermission method`);
    return auth.user.hasPermission(permission);
  }

  // Méthode 3: Si auth.user.permissions est un tableau, vérifier directement
  if (
    auth.user &&
    auth.user.permissions &&
    Array.isArray(auth.user.permissions)
  ) {
    // console.debug(
    //   `Using auth.user.permissions array (${auth.user.permissions.length} permissions)`
    // );
    const hasPermission = auth.user.permissions.includes(permission);
    // console.debug(
    //   `Permission "${permission}" ${
    //     hasPermission ? "found" : "not found"
    //   } in user permissions`
    // );
    return hasPermission;
  }

  // Aucune des méthodes n'a fonctionné
  console.warn(
    `No valid permission checking mechanism found for: ${permission}`,
  );
  return false;
};

const formatNumber = (value: number): string => {
  return Number(value).toLocaleString("fr-FR", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  });
};

const toNumberWithDecimalSupport = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;
  const normalized =
    typeof value === "string"
      ? value.replace(/\s/g, "").replace(/,/g, ".")
      : value;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const sanitizeDecimalInput = (
  value: any,
  decimalPlaces = QUANTITY_DECIMAL_PLACES,
): string => {
  if (value === null || value === undefined) return "";
  const normalized = String(value)
    .replace(/\s/g, "")
    .replace(/,/g, ".")
    .replace(/[^0-9.]/g, "");
  const [integerPart, ...fractionParts] = normalized.split(".");
  const fractionPart = fractionParts.join("").slice(0, decimalPlaces);
  return fractionPart ? `${integerPart || "0"}.${fractionPart}` : integerPart;
};

const normalizePositiveQuantity = (
  value: any,
  fallback = MIN_SALE_QUANTITY,
): number => {
  const parsed = toNumberWithDecimalSupport(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Number(parsed.toFixed(QUANTITY_DECIMAL_PLACES));
};

const resolveQuantityValue = (...values: any[]): number | undefined => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const parsed = toNumberWithDecimalSupport(value);
    if (Number.isFinite(parsed)) {
      return Number(parsed.toFixed(QUANTITY_DECIMAL_PLACES));
    }
  }
  return undefined;
};

const formatQuantity = (value: any): string =>
  toNumberWithDecimalSupport(value).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: QUANTITY_DECIMAL_PLACES,
  });

type StockManagementMode = "standard" | "pre_order" | "unlimited";

const normalizeStockManagementMode = (mode: unknown): StockManagementMode => {
  const normalized = String(mode || "standard")
    .trim()
    .toLowerCase();
  if (normalized === "pre_order" || normalized === "unlimited") {
    return normalized;
  }
  return "standard";
};

const resolveUnitMetadata = (source: any) => {
  const unitId =
    source?.resolved_unit_id ?? source?.unit_id ?? source?.unit?.id ?? null;
  const unitName = source?.unit_name || source?.unit?.name;
  const unitShortName = source?.unit_short_name || source?.unit?.short_name;

  return {
    unit_id: unitId,
    unit_name: unitName,
    unit_short_name: unitShortName,
    unit:
      unitId !== null || unitName || unitShortName
        ? {
            id: unitId,
            name: unitName,
            short_name: unitShortName,
          }
        : undefined,
  };
};

const requiresStrictStockCheck = (item: {
  stock_management_mode?: unknown;
  sellable_without_production?: unknown;
}): boolean =>
  normalizeStockManagementMode(item?.stock_management_mode) === "standard" &&
  !item?.sellable_without_production;

interface Product {
  id_produit: number;
  item_type?: "product" | "service";
  product_id?: number | null;
  service_id?: number | null;
  nom_produit: string;
  prix_vente: number;
  quantite_stock?: number;
  stock_management_mode?: StockManagementMode;
  product_type?: string;
  production_kind?: "finished" | "semi_finished" | string | null;
  sellable_without_production?: boolean;
  code_barre?: string;
  duration_minutes?: number | null;
  image?: string;
  unit_id?: number | null;
  unit_name?: string;
  unit_short_name?: string;
  unit?: {
    id?: number | null;
    name?: string;
    short_name?: string;
  };
}

const normalizeProductDetailForSale = (item: any): Product => ({
  id_produit: Number(item?.id ?? item?.id_produit ?? item?.product_id),
  item_type: "product",
  product_id: Number(item?.id ?? item?.id_produit ?? item?.product_id),
  service_id: null,
  nom_produit:
    item?.name || item?.nom_produit || item?.product_name || "Produit inconnu",
  prix_vente: Number(item?.sales_price ?? item?.prix_vente ?? 0) || 0,
  quantite_stock: resolveQuantityValue(
    item?.current_stock,
    item?.quantite_stock,
    item?.stock,
  ),
  stock_management_mode: normalizeStockManagementMode(
    item?.stock_management_mode,
  ),
  product_type: String(item?.product_type || "single").toLowerCase(),
  production_kind:
    String(item?.product_type || "").toLowerCase() === "production"
      ? String(item?.production_kind || "finished").toLowerCase()
      : null,
  sellable_without_production:
    item?.sellable_without_production === true ||
    Number(item?.sellable_without_production) === 1,
  code_barre:
    item?.barcode || item?.barcode_value || item?.item_code || undefined,
  image:
    item?.image ||
    item?.image_url ||
    item?.product_image ||
    item?.photo ||
    undefined,
  unit_id: item?.unit_id ?? item?.unit?.id ?? null,
  unit_name: item?.unit_name || item?.unit?.name,
  unit_short_name: item?.unit_short_name || item?.unit?.short_name,
  unit:
    item?.unit_id || item?.unit_name || item?.unit_short_name
      ? {
          id: item?.unit_id ?? item?.unit?.id ?? null,
          name: item?.unit_name || item?.unit?.name,
          short_name: item?.unit_short_name || item?.unit?.short_name,
        }
      : undefined,
});

interface ProduitVendu {
  produit_id: number;
  item_type?: "product" | "service";
  service_id?: number | null;
  nom_produit: string;
  quantite: number;
  prix_unitaire_HT: number;
  remise?: number;
  taxe?: number | null;
  montant_taxe?: number;
  quantite_stock?: number;
  stock_management_mode?: StockManagementMode;
  product_type?: string;
  production_kind?: "finished" | "semi_finished" | string | null;
  sellable_without_production?: boolean;
  product_id?: number;
  product_name?: string;
  quantity?: number;
  unit_price?: number;
  discount_rate?: number;
  tax_id?: number;
  total_tax?: number;
  subtotal?: number;
  image?: string;
  unit_id?: number | null;
  unit_name?: string;
  unit_short_name?: string;
  unit?: {
    id?: number | null;
    name?: string;
    short_name?: string;
  };
}

interface VenteFormData {
  id?: number;
  Date_Facture: string;
  Statut_Vente: string;
  Client_ID: string;
  produitsVendus: ProduitVendu[];
  remise_globale: number;
  manual_discount?: number;
  loyalty_discount?: number;
  loyalty_discount_rate?: number;
  loyalty_discount_applied?: boolean;
  termes_conditions?: string;
  remarques?: string;
  invoice_subject?: string | null;
  paid_amount?: number; // CORRECTION: Ajout pour éviter les erreurs NULL
  payment_status?: string;
  invoice_type?: string;
}

interface DateRange {
  start: string;
  end: string;
}

interface SalesSummary {
  total_sales_count: number;
  total_sales_amount: number;
  paid_amount: number;
  unpaid_amount: number;
  partially_paid_amount: number;
  partially_due_amount: number;
  total_received_amount: number;
  total_due_amount: number;
}

interface VentesState {
  ventes: any[];
  clients: any[];
  produits: Product[];
  taxes: any[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  itemsPerPage: number;
  totalRecords: number;
  searchTerm: string;
  dateRange: DateRange;
  selectedClient: any | null;
  showForm: boolean;
  formData: VenteFormData;
  showDeleted: boolean;
  activeTab: string;
  salesChannel: SalesChannel;
  selectedVenteIds: number[];
  salesSummary: SalesSummary;
}

type VentesAction =
  | { type: "SET_LOADING"; payload: boolean }
  | {
      type: "SET_VENTES";
      payload: { ventes: any[]; total: number; summary?: Partial<SalesSummary> };
    }
  | { type: "DELETE_VENTE"; payload: number }
  | { type: "SET_CLIENTS"; payload: any[] }
  | { type: "SET_PRODUITS"; payload: Product[] }
  | { type: "SET_TAXES"; payload: any[] }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_PAGE"; payload: number }
  | { type: "SET_SEARCH"; payload: string }
  | { type: "SET_DATE_RANGE"; payload: DateRange }
  | { type: "SET_SELECTED_CLIENT"; payload: any }
  | { type: "TOGGLE_FORM"; payload: boolean }
  | { type: "SET_FORM_DATA"; payload: VenteFormData }
  | { type: "UPDATE_FORM_DATA"; payload: VenteFormData }
  | { type: "TOGGLE_SHOW_DELETED"; payload: boolean }
  | { type: "SET_ACTIVE_TAB"; payload: string }
  | { type: "SET_SALES_CHANNEL"; payload: SalesChannel }
  | { type: "SET_ITEMS_PER_PAGE"; payload: number }
  | { type: "SET_SELECTED_VENTE_IDS"; payload: number[] }
  | { type: "ADD_SELECTED_VENTE_ID"; payload: number }
  | { type: "REMOVE_SELECTED_VENTE_ID"; payload: number }
  | { type: "CLEAR_SELECTED_VENTE_IDS" }
  | {
      type: "UPDATE_VENTE_DETAIL";
      payload: { id: React.Key; produitsVendus: ProduitVendu[] };
    };

const sectionStyle = {
  border: "1px solid #ccc",
  padding: "16px",
  borderRadius: "4px",
  marginBottom: "16px",
};

const BASE_URL = API_CONFIG.BASE_URL;

const normalizeSalesSummary = (
  summary?: Partial<SalesSummary> | null,
): SalesSummary => {
  const unpaidAmount = toNumberWithDecimalSupport(summary?.unpaid_amount);
  const explicitTotalDue = toNumberWithDecimalSupport(
    summary?.total_due_amount,
  );
  const partiallyDueAmount = toNumberWithDecimalSupport(
    summary?.partially_due_amount ??
      Math.max(explicitTotalDue - unpaidAmount, 0),
  );

  return {
    total_sales_count: toNumberWithDecimalSupport(summary?.total_sales_count),
    total_sales_amount: toNumberWithDecimalSupport(summary?.total_sales_amount),
    paid_amount: toNumberWithDecimalSupport(summary?.paid_amount),
    unpaid_amount: unpaidAmount,
    partially_paid_amount: toNumberWithDecimalSupport(
      summary?.partially_paid_amount,
    ),
    partially_due_amount: partiallyDueAmount,
    total_received_amount: toNumberWithDecimalSupport(
      summary?.total_received_amount ?? summary?.paid_amount,
    ),
    total_due_amount: toNumberWithDecimalSupport(
      summary?.total_due_amount ?? unpaidAmount + partiallyDueAmount,
    ),
  };
};

const emptySalesSummary = normalizeSalesSummary(null);

const mergeSalesSummaryWithPending = (
  summary: Partial<SalesSummary> | null | undefined,
  pendingOrders: any[],
): SalesSummary => {
  const next = normalizeSalesSummary(summary);

  pendingOrders.forEach((order) => {
    const total = toNumberWithDecimalSupport(order.total);
    const paid = toNumberWithDecimalSupport(order.paid_amount);
    const due = Math.max(
      toNumberWithDecimalSupport(order.due_amount ?? total - paid),
      0,
    );
    const normalizedStatus = String(order.payment_status || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

    next.total_sales_count += 1;
    next.total_sales_amount += total;
    next.total_received_amount += paid;
    next.total_due_amount += due;

    if (normalizedStatus === "paye" || normalizedStatus === "paid") {
      next.paid_amount += total;
    } else if (
      normalizedStatus === "partiellement paye" ||
      normalizedStatus === "partiel" ||
      normalizedStatus === "partial"
    ) {
      next.partially_paid_amount += total;
      next.partially_due_amount += due;
    } else {
      next.unpaid_amount += total;
    }
  });

  return next;
};

// Helper function to get authentication headers
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const getResponseErrorMessage = async (
  response: Response,
  fallback: string,
): Promise<string> => {
  const rawBody = await response.text().catch(() => "");
  let payload: any = null;
  if (rawBody) {
    try {
      payload = JSON.parse(rawBody);
    } catch {
      payload = null;
    }
  }

  const jsonMessage = payload?.message || payload?.error;
  if (typeof jsonMessage === "string" && jsonMessage.trim()) {
    return jsonMessage.trim();
  }

  const contentType = response.headers.get("content-type") || "";
  const plainText = rawBody.trim();
  if (
    contentType.toLowerCase().startsWith("text/plain") &&
    plainText &&
    plainText.length <= 300
  ) {
    return plainText;
  }

  return `${fallback} (HTTP ${response.status})`;
};

const buildOfflineOrderRecord = (entry: any) => {
  const order = entry?.body || {};
  const tempId = entry?.meta?.tempId || `offline-${entry.id}`;
  return {
    id: tempId,
    invoice_number: order.invoice_number || order.client_request_id || tempId,
    order_date: order.order_date || new Date().toISOString(),
    user_id: order.user_id,
    warehouse_id: order.warehouse_id,
    warehouse_name: order.warehouse_name || "",
    order_status: order.order_status || "En attente",
    payment_status: order.payment_status || "Non payé",
    record_source: "internal",
    sales_channel: order.order_type === "POS" ? "pos" : "manual",
    paid_amount: order.paid_amount || 0,
    total: order.total || 0,
    order_type: order.order_type || entry?.meta?.orderType || "sales",
    is_deleted: 0,
    is_deletable: 0,
    __offline: true,
    __queue_id: entry.id,
  };
};

const getPendingOrdersFromQueue = async () => {
  const queue = await getQueueEntriesForCurrentContext();
  return queue
    .filter((entry: any) => entry?.meta?.type === "order")
    .map(buildOfflineOrderRecord);
};

// Exporté pour être réutilisé par l'écran mobile (src/mobile/ventes/VentesMobile.jsx),
// qui bénéficie ainsi du cache offline et de la file d'attente hors-ligne.
export const api = {
  // --- Mise à jour de fetchVentes pour combiner tous les filtres ---
  async fetchVentes(params: any) {
    const queryParams: any = {
      order_type: "sales,POS",
      sort_by: "order_date",
      order: "desc",
      include_summary: "true",
    };

    const search = String(params.search ?? params.Numero_Facture ?? "").trim();
    if (search) queryParams.search = search;
    if (params.user_id) queryParams.user_id = params.user_id;
    if (params.dateDebut) queryParams.dateDebut = params.dateDebut;
    if (params.dateFin) queryParams.dateFin = params.dateFin;
    if (params.page) queryParams.page = params.page;
    if (params.limit) queryParams.limit = params.limit;
    if (params.warehouse) queryParams.warehouse = params.warehouse;
    if (params.company_id) queryParams.company_id = params.company_id;
    if (params.include_deleted)
      queryParams.include_deleted = params.include_deleted;
    if (params.payment_status)
      queryParams.payment_status = params.payment_status;
    if (params.sales_channel)
      queryParams.sales_channel = params.sales_channel;

    const qs = new URLSearchParams(queryParams).toString();
    const cacheKey = buildCacheKey("ventes:unified-list", queryParams);

    const result = await fetchWithCache(
      cacheKey,
      async () => {
        const response = await fetch(`${BASE_URL}/api/orders/sales-ledger?${qs}`, {
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Erreur HTTP " + response.status + ": " + errorText);
          throw new Error(
            response.status + " " + response.statusText + ": " + errorText,
          );
        }

        const data = await response.json();

        if (!data || (!data.orders && !Array.isArray(data))) {
          console.error("Format de r?ponse invalide:", data);
          throw new Error("Format de r?ponse invalide");
        }

        const ventesArray = Array.isArray(data.orders)
          ? data.orders
          : Array.isArray(data)
            ? data
            : [];
        const total = data.total || ventesArray.length;
        const summary = normalizeSalesSummary(data.summary);

        return { data: ventesArray, total, summary };
      },
      { data: [], total: 0, summary: emptySalesSummary },
    );

    const requestedChannel = params.sales_channel || "all";
    const pending = ["all", "manual"].includes(requestedChannel)
      ? await getPendingOrdersFromQueue()
      : [];
    const pendingForCurrentPage = Number(params.page || 1) === 1 ? pending : [];
    const merged = [...pendingForCurrentPage, ...(result?.data || [])];
    const summary = mergeSalesSummaryWithPending(
      result?.summary || emptySalesSummary,
      pending,
    );
    return {
      data: merged,
      total: (result?.total || 0) + pending.length,
      summary,
    };
  },

  async fetchClients(
    companyId: number | string | null,
    warehouseId: number | string | null,
  ) {
    try {
      let url = `${BASE_URL}/api/users/customers`;
      const params = new URLSearchParams();

      if (companyId) {
        params.append("companyId", String(companyId));
      }

      if (warehouseId) {
        params.append("warehouseId", String(warehouseId));
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const cacheKey = buildCacheKey("ventes:clients", {
        companyId,
        warehouseId,
      });
      const data = await fetchWithCache(cacheKey, async () => {
        const response = await fetch(url, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          console.error(
            `Erreur lors de la r?cup?ration des clients: ${response.status}`,
          );
          throw new Error(
            `Erreur lors de la r?cup?ration des clients: ${response.status}`,
          );
        }
        return await response.json();
      }, []);

      // D?doublonnage des clients (correction des doublons en BDD)
      const { deduplicateEntities, countDuplicates } =
        await import("../utils/entityUtils");
      const uniqueClients = deduplicateEntities(data);

      // Log pour monitoring (seulement si des doublons d?tect?s)
      const stats = countDuplicates(data);
      if (stats.duplicates > 0) {
        console.warn(
          `[fetchClients] Doublons d?tect?s et corrig?s: ${stats.duplicates} sur ${stats.total} entr?es`,
        );
      }

      return uniqueClients;
    } catch (error) {
      console.error("Erreur lors de la r?cup?ration des clients:", error);
      throw error;
    }
  },
  async fetchProduits(params: any = {}) {
    // Construction dynamique des paramètres de la requête
    const queryParams: any = {};

    if (params.search) queryParams.search = params.search;
    if (params.limit) queryParams.limit = params.limit;
    if (params.page) queryParams.page = params.page;
    if (params.warehouse) {
      queryParams.warehouse = params.warehouse;
    }
    if (params.barcode) queryParams.barcode = params.barcode;
    if (params.company_id) queryParams.company_id = params.company_id;
    if (params.categorie) queryParams.categorie = params.categorie;
    queryParams.sellable_only = "true";

    const queryString = new URLSearchParams(queryParams).toString();
    const cacheKey = buildCacheKey("ventes:produits", queryParams);
    try {
      const data = await fetchWithCache(cacheKey, async () => {
        const response = await fetch(
          `${BASE_URL}/api/produits?${queryString}`,
          {
            headers: getAuthHeaders(),
          },
        );
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Erreur HTTP ${response.status}: ${errorText}`);
          throw new Error(
            `Erreur lors de la récupération des produits: ${response.status} - ${errorText}`,
          );
        }
        return await response.json();
      }, []);

      let servicesData: any[] = [];
      if (!params.barcode) {
        const serviceQuery = new URLSearchParams({
          ...(params.search ? { search: String(params.search) } : {}),
          ...(params.warehouse
            ? { warehouse_id: String(params.warehouse) }
            : {}),
          ...(params.company_id
            ? { company_id: String(params.company_id) }
            : {}),
          limit: String(Math.min(Number(params.limit) || 200, 500)),
        });
        const serviceCacheKey = buildCacheKey("ventes:services", {
          search: params.search || "",
          warehouse_id: params.warehouse || "",
          company_id: params.company_id || "",
        });
        const servicePayload = await fetchWithCache(
          serviceCacheKey,
          async () => {
            const response = await fetch(
              `${BASE_URL}/api/services/sellable?${serviceQuery.toString()}`,
              { headers: getAuthHeaders() },
            );
            if (!response.ok) {
              throw new Error(
                `Erreur lors de la récupération des services: ${response.status}`,
              );
            }
            return response.json();
          },
          [],
        );
        servicesData = Array.isArray(servicePayload?.services)
          ? servicePayload.services
          : [];
      }

      // Vérifier la structure de la réponse
      let productsData = [];
      let total = 0;

      if (data && data.products && Array.isArray(data.products)) {
        productsData = data.products;
        total = data.total || productsData.length;
      } else if (Array.isArray(data)) {
        productsData = data;
        total = data.length;
      } else if (typeof data === "object" && data !== null) {
        // Essayer de trouver une propriété qui pourrait contenir des produits
        const possibleArrayKeys = Object.keys(data).filter(
          (key) =>
            Array.isArray(data[key]) &&
            data[key].length > 0 &&
            typeof data[key][0] === "object",
        );

        if (possibleArrayKeys.length > 0) {
          const key = possibleArrayKeys[0];
          productsData = data[key];
          total = productsData.length;
        } else {
          console.warn(
            "Format inconnu de réponse, aucun tableau de produits trouvé",
          );
        }
      }

      // Ajouter des logs pour le débogage

      if (productsData.length > 0) {
      }

      // Normaliser les produits au format attendu par l'application
      const normalizedProducts = productsData.map((item: any) => {
        return {
          id_produit: item.id_produit || item.id || item.product_id,
          item_type: "product" as const,
          product_id: item.id_produit || item.id || item.product_id,
          service_id: null,
          nom_produit:
            item.nom_produit ||
            item.name ||
            item.product_name ||
            "Produit inconnu",
          prix_vente:
            typeof item.prix_vente === "number"
              ? item.prix_vente
              : typeof item.sales_price === "number"
                ? item.sales_price
                : typeof item.unit_price === "number"
                  ? item.unit_price
                  : 0,
          quantite_stock: resolveQuantityValue(
            item.quantite_stock,
            item.current_stock,
            item.quantity,
            item.stock,
          ),
          stock_management_mode: normalizeStockManagementMode(
            item.stock_management_mode,
          ),
          product_type: String(item.product_type || "single").toLowerCase(),
          production_kind:
            String(item.product_type || "").toLowerCase() === "production"
              ? String(item.production_kind || "finished").toLowerCase()
              : null,
          sellable_without_production:
            item.sellable_without_production === true ||
            Number(item.sellable_without_production) === 1,
          code_barre:
            item.code_barre ||
            item.barcode_value ||
            item.barcode ||
            item.item_code,
          unit_id: item.unit_id ?? item.unit?.id ?? null,
          unit_name: item.unit_name || item.unit?.name,
          unit_short_name: item.unit_short_name || item.unit?.short_name,
          unit: item.unit
            ? {
                id: item.unit.id ?? item.unit_id ?? null,
                name: item.unit.name ?? item.unit_name,
                short_name: item.unit.short_name ?? item.unit_short_name,
              }
            : undefined,
        };
      });

      const normalizedServices: Product[] = servicesData.map((service: any) => ({
        id_produit: -Number(service.id || service.service_id),
        item_type: "service",
        product_id: null,
        service_id: Number(service.id || service.service_id),
        nom_produit: service.name || "Service inconnu",
        prix_vente: Number(service.base_price || 0),
        stock_management_mode: "unlimited",
        product_type: "service",
        production_kind: null,
        sellable_without_production: false,
        code_barre: service.code || undefined,
        duration_minutes: service.duration_minutes ?? null,
        unit_id: null,
      }));

      return {
        data: [...normalizedProducts, ...normalizedServices],
        total: total + normalizedServices.length,
      };
    } catch (error) {
      console.error("Erreur lors de la récupération des produits:", error);
      throw error;
    }
  },
  async fetchUnits(companyId?: number) {
    const queryParams = new URLSearchParams({
      limit: "500",
      ...(companyId ? { company_id: String(companyId) } : {}),
    });

    const response = await fetch(`${BASE_URL}/api/units?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error(response.statusText);
    const data = await response.json();
    return data.units || [];
  },
  async fetchTaxes(companyId?: number) {
    const queryParams = new URLSearchParams({
      limit: "500",
      ...(companyId ? { companyId: String(companyId) } : {}),
    });
    const cacheKey = buildCacheKey("ventes:taxes", { companyId });
    const data = await fetchWithCache(
      cacheKey,
      async () => {
        const response = await fetch(`${BASE_URL}/api/taxes?${queryParams}`, {
          headers: getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error(
            `Erreur lors de la récupération des taxes: ${response.status}`,
          );
        }
        return await response.json();
      },
      { taxes: [] },
    );
    return data.taxes || [];
  },
  async fetchLoyaltyEligibility(params: {
    warehouse_id: number;
    user_id: number;
    amount_ht: number;
    company_id?: number;
  }) {
    const requestParams = new URLSearchParams();
    requestParams.set("warehouse_id", String(params.warehouse_id));
    requestParams.set("user_id", String(params.user_id));
    requestParams.set("amount_ht", String(params.amount_ht || 0));
    if (params.company_id) {
      requestParams.set("company_id", String(params.company_id));
    }

    const response = await fetch(
      `${BASE_URL}/api/orders/loyalty-eligibility?${requestParams.toString()}`,
      {
        headers: getAuthHeaders(),
      },
    );

    if (!response.ok) {
      throw new Error(
        `Erreur lors de la vérification fidélité: ${response.status}`,
      );
    }

    return await response.json();
  },
  async createVente(data: any) {
    try {
      const manualDiscount = Number(
        data.manual_discount ?? data.remise_globale ?? 0,
      );
      const loyaltyDiscountApplied =
        data.loyalty_discount_applied === true ||
        data.loyalty_discount_applied === 1 ||
        data.loyalty_discount_applied === "1" ||
        data.loyalty_discount_applied === "true";
      const loyaltyDiscount = loyaltyDiscountApplied
        ? Number(data.loyalty_discount || 0)
        : 0;
      const loyaltyDiscountRate = loyaltyDiscountApplied
        ? Number(data.loyalty_discount_rate || 0)
        : 0;
      const totalDiscount = Math.max(0, manualDiscount + loyaltyDiscount);

      // Convertir les données au format attendu par l'API de commandes
      const orderData = {
        company_id: data.company_id, // Utiliser selectedCompany du contexte (fourni ligne 1222)
        warehouse_id: data.warehouse_id,
        order_date: data.Date_Facture,
        order_type: "sales", // Spécifier que c'est une vente
        manual_invoice_number: data.manual_invoice_number,
        invoice_type: data.invoice_type || "standard",
        user_id: Number(data.Client_ID), // Forcer la conversion en nombre
        discount: totalDiscount,
        manual_discount: manualDiscount,
        loyalty_discount: loyaltyDiscount,
        loyalty_discount_rate: loyaltyDiscountRate,
        loyalty_discount_applied: loyaltyDiscountApplied,
        notes: data.remarques || "",
        invoice_subject: normalizeInvoiceSubject(data.invoice_subject),
        terms_condition: data.termes_conditions || "",
        order_status: data.Statut_Vente || "Commandé",
        payment_status: data.payment_status || "Non payé",
        tax_amount: data.Montant_Taxe || 0,
        tax_rate: 0,
        shipping: 0,
        subtotal: data.Montant_Total_HT || 0, // CORRECTION: Utiliser le montant base HT (avant remise)
        total: data.Montant_TTC || 0,
        paid_amount: 0,
        due_amount: data.Montant_TTC || 0,
        initial_payment: data.initial_payment,
        total_items: data.produitsVendus?.length || 0,
        total_quantity:
          data.produitsVendus?.reduce(
            (sum: number, p: ProduitVendu) => sum + p.quantite,
            0,
          ) || 0,
        items: (data.produitsVendus || []).map((p: ProduitVendu) => ({
          item_type: p.item_type || "product",
          product_id:
            p.item_type === "service" ? null : p.product_id || p.produit_id,
          service_id: p.item_type === "service" ? p.service_id : null,
          item_name: p.nom_produit,
          unit_id: p.unit_id ?? p.unit?.id ?? null,
          quantity: p.quantite,
          unit_price: p.prix_unitaire_HT,
          discount_rate: p.remise || 0,
          tax_id: p.taxe || null,
          tax_rate: 0,
          tax_type: "amount",
          total_tax: p.montant_taxe || 0,
          total_discount: p.remise || 0,
          subtotal: p.prix_unitaire_HT * p.quantite - (p.remise || 0), // Sous-total après remise individuelle
        })),
      };

      if (isOffline()) {
        const tempId = buildTempId("vente");
        await enqueueRequest({
          method: "POST",
          url: `${BASE_URL}/api/orders`,
          body: { ...orderData, client_request_id: tempId },
          meta: { type: "order", tempId, orderType: "sales" },
        });
        return { id: tempId, orderId: tempId, offline: true };
      }

      // Appel API pour créer la vente
      const response = await fetch(`${BASE_URL}/api/orders`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Erreur lors de la création de la vente: ${response.status}`;
        let errorPayload: any = null;
        try {
          errorPayload = JSON.parse(errorText);
          errorMessage =
            errorPayload.details || errorPayload.error || errorMessage;
        } catch (_parseError) {
          if (errorText.trim()) errorMessage = errorText;
        }
        console.error(
          `Erreur HTTP ${response.status} lors de la création de la vente:`,
          errorText,
        );
        const requestError: any = new Error(errorMessage);
        requestError.code = errorPayload?.code;
        requestError.stock_items = errorPayload?.stock_items;
        throw requestError;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Erreur détaillée lors de la création de la vente:", error);
      throw error;
    }
  },
  async updateVente(id: number, data: any) {
    try {
      const manualDiscount = Number(
        data.manual_discount ?? data.remise_globale ?? 0,
      );
      const loyaltyDiscountApplied =
        data.loyalty_discount_applied === true ||
        data.loyalty_discount_applied === 1 ||
        data.loyalty_discount_applied === "1" ||
        data.loyalty_discount_applied === "true";
      const loyaltyDiscount = loyaltyDiscountApplied
        ? Number(data.loyalty_discount || 0)
        : 0;
      const loyaltyDiscountRate = loyaltyDiscountApplied
        ? Number(data.loyalty_discount_rate || 0)
        : 0;
      const totalDiscount = Math.max(0, manualDiscount + loyaltyDiscount);

      // Convertir les données au format attendu par l'API de commandes
      const orderData = {
        company_id: data.company_id, // Utiliser selectedCompany du contexte (fourni ligne 1222)
        warehouse_id: data.warehouse_id,
        order_date: data.Date_Facture,
        order_type: "sales", // Spécifier que c'est une vente
        invoice_type: data.invoice_type || "standard",
        user_id: Number(data.Client_ID),
        discount: totalDiscount,
        manual_discount: manualDiscount,
        loyalty_discount: loyaltyDiscount,
        loyalty_discount_rate: loyaltyDiscountRate,
        loyalty_discount_applied: loyaltyDiscountApplied,
        notes: data.remarques || "",
        ...(Object.prototype.hasOwnProperty.call(data, "invoice_subject")
          ? { invoice_subject: normalizeInvoiceSubject(data.invoice_subject) } : {}),
        terms_condition: data.termes_conditions || "",
        order_status: data.Statut_Vente || "Commandé",
        payment_status: data.payment_status || "Non payé",
        // CORRECTION CRITIQUE: Envoyer paid_amount pour éviter les erreurs NULL
        paid_amount: parseFloat(data.paid_amount) || 0,
        tax_amount: data.Montant_Taxe || 0,
        tax_rate: 0,
        shipping: 0,
        subtotal: data.Montant_Total_HT || 0, // CORRECTION: Utiliser le montant base HT (avant remise)
        total: data.Montant_TTC || 0,
        total_items: data.produitsVendus?.length || 0,
        total_quantity:
          data.produitsVendus?.reduce(
            (sum: number, p: ProduitVendu) => sum + p.quantite,
            0,
          ) || 0,
        items: (data.produitsVendus || []).map((p: ProduitVendu) => ({
          item_type: p.item_type || "product",
          product_id:
            p.item_type === "service" ? null : p.product_id || p.produit_id,
          service_id: p.item_type === "service" ? p.service_id : null,
          item_name: p.nom_produit,
          unit_id: p.unit_id ?? p.unit?.id ?? null,
          quantity: p.quantite,
          unit_price: p.prix_unitaire_HT,
          discount_rate: p.remise || 0,
          tax_id: p.taxe || null,
          tax_rate: 0,
          tax_type: "amount",
          total_tax: p.montant_taxe || 0,
          total_discount: p.remise || 0,
          subtotal: p.prix_unitaire_HT * p.quantite - (p.remise || 0), // Sous-total après remise individuelle
        })),
      };

      if (isOffline()) {
        await enqueueRequest({
          method: "PUT",
          url: `${BASE_URL}/api/orders/${id}`,
          body: orderData,
          meta: { type: "order_update", targetId: id },
        });
        return { id, offline: true };
      }

      const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorPayload: any = null;
        let errorMessage = `Erreur lors de la mise à jour de la vente: ${response.status}`;
        try {
          errorPayload = JSON.parse(errorText);
          errorMessage =
            errorPayload.details ||
            errorPayload.message ||
            errorPayload.error ||
            errorMessage;
        } catch (_parseError) {
          // Conserver le message HTTP générique si la réponse n'est pas en JSON.
        }
        console.error(
          `Erreur HTTP ${response.status} lors de la mise à jour de la vente:`,
          errorText,
        );
        const requestError: any = new Error(errorMessage);
        requestError.code = errorPayload?.code;
        requestError.stock_items = errorPayload?.stock_items;
        throw requestError;
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error(
        "Erreur détaillée lors de la mise à jour de la vente:",
        error,
      );
      throw error;
    }
  },
  async deleteVente(id: number) {
    if (isOffline()) {
      await enqueueRequest({
        method: "DELETE",
        url: `${BASE_URL}/api/orders/${id}`,
        body: null,
        meta: { type: "order_delete", targetId: id },
      });
      return { id, offline: true };
    }

    const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(
        `Erreur lors de la suppression de la vente: ${response.status}`,
      );
    }

    return await response.json();
  },
  async fetchVenteDetail(
    id: string | number,
    params: { company_id?: number; warehouse_id?: number } = {},
  ) {
    try {
      const cacheKey = buildCacheKey("ventes:detail", {
        id,
        company_id: params.company_id,
        warehouse_id: params.warehouse_id,
      });
      const data = await fetchWithCache(
        cacheKey,
        async () => {
          const requestParams = new URLSearchParams();
          if (params.company_id) {
            requestParams.set("company_id", String(params.company_id));
          }
          if (params.warehouse_id) {
            requestParams.set("warehouse_id", String(params.warehouse_id));
          }

          const endpoint = `${BASE_URL}/api/orders/${id}${
            requestParams.toString() ? `?${requestParams.toString()}` : ""
          }`;

          const response = await fetch(endpoint, {
            headers: getAuthHeaders(),
          });
          if (!response.ok) {
            throw new Error(
              `Erreur lors de la récupération de la vente: ${response.status}`,
            );
          }
          return await response.json();
        },
        null,
      );

      if (!data) {
        throw new Error("Aucune donnée disponible hors ligne");
      }

      // Gérer les différents formats de réponse possibles
      let orderData = data;
      if (data.order) {
        orderData = data.order;
      }

      // S'assurer que les champs nécessaires sont présents
      return {
        ...orderData,
        notes: orderData.notes || "",
        terms_condition: orderData.terms_condition || "",
        warehouse_id: orderData.warehouse_id || orderData.warehouse || 1,
        produitsVendus: orderData.items || orderData.order_items || [],
        created_by_name:
          orderData.created_by_name || orderData.staff_name || "SysAdmin",
      };
    } catch (error) {
      console.error(`Erreur lors de la récupération de la vente ${id}:`, error);
      throw error;
    }
  },
  async fetchUnifiedSaleDetail(
    sale: any,
    params: { company_id?: number; warehouse_id?: number } = {},
  ) {
    if (!isEcommerceSale(sale)) {
      return this.fetchVenteDetail(sale?.source_id ?? sale?.id, params);
    }

    const sourceId = Number(sale?.source_id);
    if (!Number.isInteger(sourceId) || sourceId <= 0) {
      throw new Error("Identifiant de vente web invalide");
    }

    const requestParams = new URLSearchParams();
    if (params.company_id) {
      requestParams.set("company_id", String(params.company_id));
    }
    if (params.warehouse_id) {
      requestParams.set("warehouse_id", String(params.warehouse_id));
    }
    const endpoint = `${BASE_URL}/api/orders/sales-ledger/ecommerce/${sourceId}${
      requestParams.toString() ? `?${requestParams.toString()}` : ""
    }`;
    const cacheKey = buildCacheKey("ventes:unified-detail", {
      sourceId,
      company_id: params.company_id,
      warehouse_id: params.warehouse_id,
    });
    const data = await fetchWithCache(
      cacheKey,
      async () => {
        const response = await fetch(endpoint, { headers: getAuthHeaders() });
        if (!response.ok) {
          throw new Error(
            `Erreur lors de la récupération de la vente web: ${response.status}`,
          );
        }
        return await response.json();
      },
      null,
    );

    if (!data?.order) {
      throw new Error("Aucune donnée disponible pour cette vente web");
    }
    return {
      ...sale,
      ...data.order,
      produitsVendus: data.items || data.order.items || [],
      payments: data.payments || data.order.payments || [],
      payment_summary:
        data.payment_summary || data.order.payment_summary || null,
    };
  },
  async fetchOrderItems(
    orderId: string | number,
    params: { company_id?: number; warehouse_id?: number } = {},
  ) {
    try {
      const cacheKey = buildCacheKey("ventes:items", {
        orderId,
        company_id: params.company_id,
        warehouse_id: params.warehouse_id,
      });
      const data = await fetchWithCache(cacheKey, async () => {
        const requestParams = new URLSearchParams();
        if (params.company_id) {
          requestParams.set("company_id", String(params.company_id));
        }
        if (params.warehouse_id) {
          requestParams.set("warehouse_id", String(params.warehouse_id));
        }

        const endpoint = `${BASE_URL}/api/orders/${orderId}/items${
          requestParams.toString() ? `?${requestParams.toString()}` : ""
        }`;

        const response = await fetch(endpoint, {
          headers: getAuthHeaders(),
        });

        if (!response.ok) {
          throw new Error(
            `Erreur lors de la récupération des éléments de la vente: ${response.status}`,
          );
        }

        return await response.json();
      }, []);

      // Gérer les différents formats de réponse possibles
      if (Array.isArray(data)) {
        return data;
      } else if (data.items && Array.isArray(data.items)) {
        return data.items;
      } else if (data.order_items && Array.isArray(data.order_items)) {
        return data.order_items;
      } else {
        console.warn(
          `Format de réponse inattendu pour les éléments de la commande ${orderId}:`,
          data,
        );
        return [];
      }
    } catch (error) {
      console.error(
        `Erreur lors de la récupération des éléments de la commande ${orderId}:`,
        error,
      );
      throw error;
    }
  },
  async fetchWarehouses() {
    const cacheKey = buildCacheKey("ventes:warehouses", {});
    const data = await fetchWithCache(cacheKey, async () => {
      const response = await fetch(`${BASE_URL}/api/warehouses`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(
          `Erreur lors de la récupération des établissements: ${response.status}`,
        );
      }

      return await response.json();
    }, []);
    return data;
  },
  async restoreVente(id: number) {
    if (isOffline()) {
      await enqueueRequest({
        method: "POST",
        url: `${BASE_URL}/api/orders/${id}/restore`,
        body: null,
        meta: { type: "order_restore", targetId: id },
      });
      return { id, offline: true };
    }

    const response = await fetch(`${BASE_URL}/api/orders/${id}/restore`, {
      method: "POST",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(
        `Erreur lors de la restauration de la vente: ${response.status}`,
      );
    }

    return await response.json();
  },
  async cancelVente(id: number) {
    if (isOffline()) {
      await enqueueRequest({
        method: "DELETE",
        url: `${BASE_URL}/api/orders/${id}`,
        body: null,
        meta: { type: "order_cancel", targetId: id },
      });
      return { id, offline: true };
    }

    const response = await fetch(`${BASE_URL}/api/orders/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(
        `Erreur lors de l'annulation de la vente: ${response.status}`,
      );
    }

    return await response.json();
  },
  async fetchWarehouse(id: number) {
    if (!id) return null;

    try {
      const cacheKey = buildCacheKey("ventes:warehouse", { id });
      const data = await fetchWithCache(
        cacheKey,
        async () => {
          const response = await fetch(`${BASE_URL}/api/warehouses/${id}`, {
            headers: getAuthHeaders(),
          });
          if (!response.ok) {
            // Si l'API ne supporte pas la récupération d'un magasin spécifique,
            // récupérer tous les magasins et filtrer
            const allWarehousesResponse = await fetch(
              `${BASE_URL}/api/warehouses`,
              {
                headers: getAuthHeaders(),
              },
            );
            if (!allWarehousesResponse.ok) {
              throw new Error(
                `Erreur lors de la récupération des établissements: ${allWarehousesResponse.status}`,
              );
            }

            const allData = await allWarehousesResponse.json();
            const warehouses = Array.isArray(allData)
              ? allData
              : allData.warehouses || [];
            const warehouse = warehouses.find(
              (w: any) => w.id === id || w.warehouse_id === id,
            );

            return warehouse || null;
          }

          return await response.json();
        },
        null,
      );
      return data;
    } catch (error) {
      console.error(`Erreur lors de la récupération de l'établissement ${id}:`, error);
      // En cas d'erreur, retourner un objet par défaut
      return {
        id: id,
        name: "ELSA Technologies",
        address: "",
        phone: "",
        email: "",
      };
    }
  },
  async fetchProductDetails(
    productIds: number[],
    params: { warehouse_id?: number; company_id?: number } = {},
  ) {
    try {
      // Si aucun ID n'est fourni, retourner un tableau vide
      if (!productIds || productIds.length === 0) {
        return [];
      }

      const normalizedIds = Array.from(
        new Set(
          productIds
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id)),
        ),
      );
      if (normalizedIds.length === 0) {
        return [];
      }

      const idsParam = normalizedIds.join(",");
      const cacheKey = buildCacheKey("ventes:products:batch", {
        ids: idsParam,
        company_id: params.company_id,
        warehouse_id: params.warehouse_id,
      });

      return await fetchWithCache(cacheKey, async () => {
        const requestParams = new URLSearchParams({
          ids: idsParam,
        });
        if (params.warehouse_id) {
          requestParams.set("warehouse_id", String(params.warehouse_id));
        }
        if (params.company_id) {
          requestParams.set("company_id", String(params.company_id));
        }

        const response = await fetch(
          `${BASE_URL}/api/produits/by-ids?${requestParams.toString()}`,
          {
            headers: getAuthHeaders(),
          },
        );

        if (!response.ok) {
          throw new Error(
            `Erreur lors de la recuperation des produits: ${response.status}`,
          );
        }

        const payload = await response.json();
        if (Array.isArray(payload?.products)) {
          return payload.products;
        }
        if (Array.isArray(payload)) {
          return payload;
        }
        return [];
      }, []);
    } catch (error) {
      console.error(
        "Erreur lors de la recuperation des details des produits:",
        error,
      );
      return [];
    }
  },
};

const initialState: VentesState = {
  ventes: [],
  clients: [],
  produits: [],
  taxes: [],
  loading: false,
  error: null,
  currentPage: 1,
  itemsPerPage: 10,
  totalRecords: 0,
  searchTerm: "",
  dateRange: { start: "", end: "" },
  selectedClient: null,
  showForm: false,
  formData: {
    Date_Facture: dayjs().format("YYYY-MM-DD"),
    Statut_Vente: "Completé",
    Client_ID: "",
    produitsVendus: [],
    remise_globale: 0,
    manual_discount: 0,
    loyalty_discount: 0,
    loyalty_discount_rate: 0,
    loyalty_discount_applied: false,
    invoice_subject: "",
  },
  showDeleted: false,
  activeTab: "all",
  salesChannel: "all",
  selectedVenteIds: [],
  salesSummary: emptySalesSummary,
};

const ventesReducer = (
  state: VentesState,
  action: VentesAction,
): VentesState => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_VENTES":
      return {
        ...state,
        ventes: action.payload.ventes,
        totalRecords: action.payload.total,
        salesSummary: normalizeSalesSummary(action.payload.summary),
      };
    case "DELETE_VENTE":
      return {
        ...state,
        ventes: state.ventes.filter((vente) => vente.id !== action.payload),
      };
    case "SET_CLIENTS":
      return { ...state, clients: action.payload };
    case "SET_PRODUITS":
      return { ...state, produits: action.payload };
    case "SET_TAXES":
      return { ...state, taxes: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_PAGE":
      return { ...state, currentPage: action.payload };
    case "SET_SEARCH":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "SET_DATE_RANGE":
      return { ...state, dateRange: action.payload, currentPage: 1 };
    case "SET_SELECTED_CLIENT":
      return { ...state, selectedClient: action.payload, currentPage: 1 };
    case "TOGGLE_FORM":
      return { ...state, showForm: action.payload };
    case "SET_FORM_DATA":
      return { ...state, formData: action.payload };
    case "UPDATE_FORM_DATA":
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload,
          Client_ID: action.payload.Client_ID || state.formData.Client_ID,
        },
      };
    case "TOGGLE_SHOW_DELETED":
      return { ...state, showDeleted: action.payload };
    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.payload };
    case "SET_SALES_CHANNEL":
      return {
        ...state,
        salesChannel: action.payload,
        selectedClient:
          action.payload === "ecommerce" ? null : state.selectedClient,
        currentPage: 1,
      };
    case "SET_ITEMS_PER_PAGE":
      return { ...state, itemsPerPage: action.payload };
    case "UPDATE_VENTE_DETAIL":
      return {
        ...state,
        ventes: state.ventes.map((vente) =>
          vente.id === action.payload.id
            ? { ...vente, produitsVendus: action.payload.produitsVendus }
            : vente,
        ),
      };
    case "SET_SELECTED_VENTE_IDS":
      return { ...state, selectedVenteIds: action.payload };
    case "ADD_SELECTED_VENTE_ID":
      return {
        ...state,
        selectedVenteIds: [...state.selectedVenteIds, action.payload],
      };
    case "REMOVE_SELECTED_VENTE_ID":
      return {
        ...state,
        selectedVenteIds: state.selectedVenteIds.filter(
          (id) => id !== action.payload,
        ),
      };
    case "CLEAR_SELECTED_VENTE_IDS":
      return { ...state, selectedVenteIds: [] };
    default:
      return state;
  }
};

export const VentesContext = createContext<{
  state: VentesState;
  dispatch: React.Dispatch<VentesAction>;
  api: typeof api;
  // Utilisons le type AuthContextType défini ci-dessus
  auth: AuthContextType | null;
  restoredDraftMeta: {
    draftId: string;
    missingItemCount: number;
    priceChangedCount: number;
  } | null;
  clearRestoredSalesDraft: () => void;
} | null>(null);

const SalesSummaryPanel: React.FC<{ summary: SalesSummary }> = ({
  summary,
}) => {
  const metrics = [
    {
      key: "revenue",
      label: "Chiffre d’affaires",
      value: summary.total_sales_amount,
      icon: <BarChartOutlined />,
      tone: "primary",
      description: "Montant total des ventes correspondant aux filtres actifs.",
    },
    {
      key: "received",
      label: "Montant encaissé",
      value: summary.total_received_amount,
      icon: <WalletOutlined />,
      tone: "success",
      description: "Montant effectivement reçu sur les ventes affichées.",
    },
    {
      key: "due",
      label: "Montant à recevoir",
      value: summary.total_due_amount,
      icon: <ClockCircleOutlined />,
      tone: "danger",
      description: "Solde restant à encaisser, paiements partiels compris.",
    },
    {
      key: "partial",
      label: "Solde sur paiements partiels",
      value: summary.partially_due_amount,
      icon: <PieChartOutlined />,
      tone: "warning",
      description:
        "Part du montant à recevoir liée aux ventes partiellement payées.",
    },
  ];
  const formatSummaryAmount = (value: number) =>
    formatNumber(value).replace(/[\u00a0\u202f]/g, " ");

  return (
    <section
      className="sales-summary"
      aria-label="Situation financière des ventes"
    >
      {metrics.map((metric) => (
        <div
          className={`sales-summary__metric sales-summary__metric--${metric.tone}`}
          key={metric.key}
          title={metric.description}
        >
          <span className="sales-summary__icon" aria-hidden="true">
            {metric.icon}
          </span>
          <span className="sales-summary__content">
            <span className="sales-summary__label">{metric.label}</span>
            <strong className="sales-summary__value">
              {formatSummaryAmount(metric.value)} CFA
            </strong>
          </span>
        </div>
      ))}
    </section>
  );
};

interface SaleItemThumbnailProps {
  imagePath?: string | null;
  itemName: string;
}

const SaleItemThumbnail: React.FC<SaleItemThumbnailProps> = ({
  imagePath,
  itemName,
}) => {
  const source = useMemo(
    () =>
      imagePath?.trim()
        ? normalizeImageUrl(imagePath, API_CONFIG.BASE_URL)
        : "",
    [imagePath],
  );
  const [failedSource, setFailedSource] = useState<string | null>(null);

  if (!source || failedSource === source) {
    return (
      <div
        className="sale-form-item__thumbnail sale-form-item__thumbnail--fallback"
        role="img"
        aria-label={`Aucune image disponible pour ${itemName}`}
      >
        <PictureOutlined aria-hidden="true" />
      </div>
    );
  }

  return (
    <img
      className="sale-form-item__thumbnail"
      src={source}
      alt=""
      onError={() => setFailedSource(source)}
    />
  );
};

interface VenteFormProps {
  visible: boolean;
  onClose: () => void;
  refreshSales: () => Promise<void>;
  warehouseName?: string | null;
}

export const VenteForm: React.FC<VenteFormProps> = ({
  visible,
  onClose,
  refreshSales,
  warehouseName,
}) => {
  const context = useContext(VentesContext);
  if (!context) throw new Error("VenteForm must be used within VentesContext");
  // Get auth from the context provided by GestionVentes
  const {
    state,
    dispatch,
    api,
    auth,
    restoredDraftMeta,
    clearRestoredSalesDraft,
  } = context;
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchKey, setSearchKey] = useState<number>(0);
  const [editingLineIndex, setEditingLineIndex] = useState<number | null>(null);
  const [stockShortageItems, setStockShortageItems] = useState<any[]>([]);
  const [filteredClients, setFilteredClients] = useState<any[]>([]);
  const [filteredTaxes, setFilteredTaxes] = useState<any[]>([]);
  const [quickClientModalOpen, setQuickClientModalOpen] =
    useState<boolean>(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const { selectedWarehouse, selectedCompany } = useSelection();
  const selectedWarehouseId = Number(
    (selectedWarehouse as any)?.id ?? selectedWarehouse,
  );
  const selectedCompanyId = Number(
    (selectedCompany as any)?.id ?? selectedCompany,
  );
  const watchedClient = Form.useWatch("Client_ID", form);
  const manualInvoiceNumber = useManualInvoiceNumber({ companyId: selectedCompanyId, warehouseId: selectedWarehouseId,
    active: visible && !state.formData.id });
  const choiceClientId = watchedClient?.value ?? watchedClient ?? state.formData.Client_ID;
  const choices = useOrderFormOptions({ orderType: "sales", action: state.formData.id ? "edit" : "create",
    active: visible, enabled: checkPermission(auth, state.formData.id
      ? "Gestion Commerciale.Ventes.Ventes.edit" : "Gestion Commerciale.Ventes.Ventes.create"),
    entityId: Number(choiceClientId) || null, items: state.formData.produitsVendus });
  const invalidateClientChoices = choices.invalidateEntity;
  const assertChoiceContext = choices.assertContext;
  const formScope = JSON.stringify([choices.scope.userId, selectedCompanyId, selectedWarehouseId]);
  const catalogStock = (record: any) => choices.products.find((product) => product.id === choiceItemId(record))?.quantite_stock;
  const openedScope = useRef(formScope);
  const contextMatches = openedScope.current === formScope;
  useEffect(() => { if (!contextMatches) onClose(); }, [contextMatches, onClose]);
  const canCreateClient = checkPermission(
    auth,
    "Gestion Commerciale.Entites.Clients.create",
  );

  // Callback pour gérer la création réussie d'un nouveau client
  const handleQuickClientSuccess = useCallback(
    (newClient: any) => {
      try { assertChoiceContext(); } catch { return; }
      // Ajouter le nouveau client à la liste locale
      setFilteredClients((prev) => [...prev, newClient]);

      // Sélectionner automatiquement le nouveau client dans le formulaire
      const clientLabel = getCommercialEntityDisplayName(
        newClient,
        `Client #${newClient.id}`,
      );
      form.setFieldsValue({
        Client_ID: {
          value: Number(newClient.id),
          label: clientLabel,
        },
      });

      // Mettre à jour l'état du formulaire dans le reducer
      dispatch({
        type: "SET_FORM_DATA",
        payload: {
          ...state.formData,
          Client_ID: String(newClient.id),
        },
      });

      void invalidateClientChoices();
    },
    [form, state.formData, dispatch, invalidateClientChoices, assertChoiceContext],
  );

  // Type l'objet auth correctement
  const typedAuth = auth as AuthContextType;

  // Determine if the form is for editing or creating
  const isEditing = !!state.formData.id;

  // Determine required permissions
  const canCreate = checkPermission(
    typedAuth,
    "Gestion Commerciale.Ventes.Ventes.create",
  );
  const canEdit = checkPermission(
    typedAuth,
    "Gestion Commerciale.Ventes.Ventes.edit",
  );
  const canCreateInitialPayment = checkPermission(
    typedAuth,
    "Gestion Commerciale.Ventes.PaiementsEntrants.create",
  );
  const hasRequiredPermission = isEditing ? canEdit : canCreate;
  const canCreatePurchase =
    checkPermission(typedAuth, PURCHASE_VIEW_PERMISSION) &&
    checkPermission(typedAuth, PURCHASE_CREATE_PERMISSION);
  const canCreateStockAdjustment =
    checkPermission(typedAuth, STOCK_ADJUST_VIEW_PERMISSION) &&
    checkPermission(typedAuth, STOCK_ADJUST_CREATE_PERMISSION);
  const canCreateProduction =
    checkPermission(typedAuth, PRODUCTION_MANAGE_VIEW_PERMISSION) &&
    checkPermission(typedAuth, PRODUCTION_MANAGE_CREATE_PERMISSION);
  const productionTerminology = getSelectedProductionTerminology(
    typedAuth?.user,
    selectedCompanyId,
  );

  const handleClose = () => {
    setEditingLineIndex(null);
    onClose();
    if (restoredDraftMeta) {
      clearRestoredSalesDraft();
    }
  };

  const openStockShortageActions = (items: any[]) => {
    const normalizedItems = buildMissingStockItems(items);
    if (normalizedItems.length === 0) return;
    setStockShortageItems(normalizedItems);
  };

  const navigateToStockResolution = (
    method: "purchase" | "adjustment" | "production",
  ) => {
    const { productionItems, purchaseItems } =
      splitStockShortageItems(stockShortageItems);
    if (method === "purchase" && !canCreatePurchase) {
      message.error("Vous n'avez pas la permission de créer un achat.");
      return;
    }
    if (method === "production" && !canCreateProduction) {
      message.error(
        `Vous n'avez pas la permission de lancer la ${productionTerminology.costContext}.`,
      );
      return;
    }

    const resolutionItems =
      method === "production"
        ? productionItems
        : method === "purchase"
          ? purchaseItems
          : stockShortageItems;
    if (resolutionItems.length === 0) {
      message.info(
        method === "production"
          ? "Aucun produit fabriqué ne nécessite de production."
          : "Aucun produit acheté ne nécessite de réapprovisionnement.",
      );
      return;
    }
    if (method === "adjustment" && !canCreateStockAdjustment) {
      message.error(
        "Vous n'avez pas la permission de créer un ajustement de stock.",
      );
      return;
    }

    const liveValues = form.getFieldsValue([
      "Date_Facture",
      "Statut_Vente",
      "Client_ID",
      "remise_globale",
      "termes_conditions",
      "remarques",
      "invoice_subject",
      "invoice_type",
    ]);
    const liveClientId =
      liveValues.Client_ID && typeof liveValues.Client_ID === "object"
        ? liveValues.Client_ID.value
        : liveValues.Client_ID || state.formData.Client_ID;
    const draftItems = mergeManualSaleDraftWithShortage(
      state.formData.produitsVendus,
      stockShortageItems,
      state.produits
    );
    const draftDate = dayjs(
      liveValues.Date_Facture || state.formData.Date_Facture
    );
    const salesDraft = saveSalesReplenishmentDraft({
      sourceType: "sale",
      sourcePath: `${location.pathname}${location.search}`,
      userId: typedAuth?.user?.id,
      companyId: selectedCompanyId,
      warehouseId: selectedWarehouseId,
      payload: {
        form_data: {
          Date_Facture: draftDate.isValid()
            ? draftDate.format("YYYY-MM-DD")
            : dayjs().format("YYYY-MM-DD"),
          Statut_Vente:
            liveValues.Statut_Vente || state.formData.Statut_Vente,
          Client_ID: liveClientId || "",
          produitsVendus: draftItems,
          remise_globale:
            Number(
              liveValues.remise_globale ?? state.formData.remise_globale
            ) || 0,
          manual_discount: Number(state.formData.manual_discount) || 0,
          termes_conditions:
            liveValues.termes_conditions ??
            state.formData.termes_conditions ??
            "",
          remarques:
            liveValues.remarques ?? state.formData.remarques ?? "",
          invoice_subject:
            liveValues.invoice_subject ?? state.formData.invoice_subject ?? "",
          invoice_type:
            liveValues.invoice_type ?? state.formData.invoice_type ?? null,
        },
      },
    });

    if (!salesDraft) {
      message.error(
        "Impossible de sécuriser temporairement le panier. Le réapprovisionnement n'a pas été ouvert.",
      );
      return;
    }

    const returnContext = {
      sales_draft_id: salesDraft.id,
      sales_draft_source: "sale",
    };

    if (method === "production") {
      const productionReplenishment =
        buildSalesProductionReplenishmentRequest({
          sourceType: "sale",
          sourceLabel: "vente en cours",
          sourcePath: `${location.pathname}${location.search}`,
          userId: typedAuth?.user?.id,
          companyId: selectedCompany,
          warehouseId: selectedWarehouse,
          items: resolutionItems,
          metadata: { return_context: returnContext },
        });
      const productionNavigation =
        buildSalesProductionReplenishmentNavigation(
          productionReplenishment,
        );

      if (!productionNavigation) {
        deleteSalesReplenishmentDraft(salesDraft.id);
        message.error(
          `Impossible de préparer le lancement de la ${productionTerminology.costContext}.`,
        );
        return;
      }

      if (
        restoredDraftMeta?.draftId &&
        restoredDraftMeta.draftId !== salesDraft.id
      ) {
        deleteSalesReplenishmentDraft(restoredDraftMeta.draftId);
      }
      setStockShortageItems([]);
      navigate(productionNavigation.path, {
        state: productionNavigation.state,
      });
      return;
    }

    const replenishment = buildStockReplenishmentRequest({
      sourceType: "sale",
      sourceLabel: "vente en cours",
      sourcePath: `${location.pathname}${location.search}`,
      companyId: selectedCompany,
      warehouseId: selectedWarehouse,
      items: resolutionItems,
      metadata: { return_context: returnContext },
    });
    if (
      restoredDraftMeta?.draftId &&
      restoredDraftMeta.draftId !== salesDraft.id
    ) {
      deleteSalesReplenishmentDraft(restoredDraftMeta.draftId);
    }
    setStockShortageItems([]);
    navigate(
      method === "purchase"
        ? "/gestion-achats/achat"
        : "/gestion-stock?tab=adjustments",
      { state: { stockReplenishment: replenishment } },
    );
  };

  // Logging pour debugging
  useEffect(() => {}, [selectedWarehouse, selectedCompany]);

  // Server choices remain separate from the user's fields and document lines.
  useEffect(() => {
    setFilteredClients(choices.entities);
  }, [choices.entities]);
  useEffect(() => {
    const selected = choices.entityResource.data?.selectedEntity;
    const value = form.getFieldValue("Client_ID");
    if (selected && Number(value?.value ?? value) === Number(selected.id) && value?.label !== selected.name) {
      form.setFieldValue("Client_ID", { value: Number(selected.id), label: selected.name });
    }
  }, [choices.entityResource.data, form]);

  // Filtrer les taxes en fonction de l'entreprise sélectionnée
  useEffect(() => {
    if (selectedCompany && state.taxes.length > 0) {
      const filtered = state.taxes.filter(
        (tax) => !tax.company_id || tax.company_id === selectedCompany,
      );
      setFilteredTaxes(filtered);
    } else {
      setFilteredTaxes(state.taxes);
    }
  }, [selectedCompany, state.taxes]);

  useEffect(() => {
    if (choices.references?.taxes) setFilteredTaxes(choices.references.taxes);
  }, [choices.references]);

  const restoredSaleShortages = useMemo(() => {
    if (!restoredDraftMeta) return [];
    return buildMissingStockItems(
      state.formData.produitsVendus
        .filter(
          (product) =>
            product.item_type !== "service" &&
            requiresStrictStockCheck(product),
        )
        .map((product) => ({
          ...product,
          required_quantity: product.quantite,
          available_quantity: product.quantite_stock,
        })),
    );
  }, [restoredDraftMeta, state.formData.produitsVendus]);
  const restoredDraftDetails = restoredDraftMeta
    ? [
        restoredDraftMeta.priceChangedCount > 0
          ? restoredDraftMeta.priceChangedCount +
            " prix actualisé" +
            (restoredDraftMeta.priceChangedCount > 1 ? "s" : "")
          : null,
        restoredDraftMeta.missingItemCount > 0
          ? restoredDraftMeta.missingItemCount +
            " article" +
            (restoredDraftMeta.missingItemCount > 1 ? "s" : "") +
            " retiré" +
            (restoredDraftMeta.missingItemCount > 1 ? "s" : "")
          : null,
      ].filter(Boolean)
    : [];

  // Calcul des totaux suivant la logique spécifiée
  const calculerTotaux = useCallback(() => {
    const produitsVendus = state.formData.produitsVendus;

    // 1. Base HT initiale = Σ (quantité × PU HT)
    const baseHT = produitsVendus.reduce(
      (sum, p) =>
        sum + (Number(p.prix_unitaire_HT) || 0) * (Number(p.quantite) || 0),
      0,
    );

    // 2. Remises commerciales (remises individuelles + remise globale)
    const remisesIndividuelles = produitsVendus.reduce(
      (sum, p) => sum + (Number(p.remise) || 0),
      0,
    );
    const remiseGlobale = Number(state.formData.remise_globale) || 0;
    const totalRemises = remisesIndividuelles + remiseGlobale;

    // 3. Montant net HT = Base HT – total_remises
    const montantNetHT = baseHT - totalRemises;

    // 4. Taxe = Montant net HT × taux_Taxe (calculée par produit sur le montant net)
    const totalTaxes = produitsVendus.reduce((sum, p) => {
      if (!p.taxe) return sum;
      const tax = filteredTaxes.find((t: any) => t.id === p.taxe);
      if (!tax) return sum;

      // Calculer la part de ce produit dans le montant net HT
      const produitBaseHT =
        (Number(p.prix_unitaire_HT) || 0) * (Number(p.quantite) || 0);
      const produitRemise = Number(p.remise) || 0;

      // Calculer la proportion de remise globale qui s'applique à ce produit
      const proportionRemiseGlobale =
        baseHT > 0 ? (produitBaseHT / baseHT) * remiseGlobale : 0;

      // Montant net HT pour ce produit
      const produitNetHT =
        produitBaseHT - produitRemise - proportionRemiseGlobale;

      // Taxe sur le montant net HT de ce produit
      const taxeRate = Number(tax.rate) || 0;
      return sum + (produitNetHT * taxeRate) / 100;
    }, 0);

    // 5. Total TTC = Montant net HT + Taxe
    const totalTTC = montantNetHT + totalTaxes;

    return {
      totalHT: baseHT, // Base HT initiale
      montantNetHT, // Montant net HT après remises
      totalRemises,
      totalTaxes,
      totalTTC,
    };
  }, [
    state.formData.produitsVendus,
    state.formData.remise_globale,
    filteredTaxes,
  ]);

  useEffect(() => {
    // Préparer les valeurs du formulaire
    const formValues: any = {
      ...state.formData,
      Date_Facture: dayjs(state.formData.Date_Facture),
    };

    // CORRECTION AMÉLIORÉE: Si Client_ID est un string (lors du chargement pour édition),
    // le convertir en objet {value, label} pour le Select avec labelInValue
    if (
      state.formData.Client_ID &&
      typeof state.formData.Client_ID === "string"
    ) {
      const clientId = state.formData.Client_ID;

      const clientData = state.clients.find(
        (c: any) => String(c.id) === clientId,
      );

      if (clientData) {
        const clientLabel =
          clientData.name ||
          clientData.company_name ||
          clientData.Nom_Raison_Sociale ||
          `Client #${clientId}`;
        formValues.Client_ID = {
          value: Number(clientId),
          label: clientLabel,
        };
      } else {
        console.warn("[VenteForm] Client NON TROUVÉ dans la liste:", clientId);
        // Même si non trouvé, créer l'objet pour éviter un champ vide
        formValues.Client_ID = {
          value: Number(clientId),
          label: `Client #${clientId}`,
        };
      }
    }

    form.setFieldsValue(formValues);
  // Initialise once per opening/document. Refreshing choices must not reset unsaved fields.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, state.formData.id, form, selectedCompanyId, selectedWarehouseId]);

  const { totalHT, montantNetHT, totalRemises, totalTaxes, totalTTC } =
    calculerTotaux();
  const calculateLineAmounts = useCallback(
    (record: any) => {
      const lineBase =
        (Number(record.prix_unitaire_HT) || 0) *
        (Number(record.quantite) || 0);
      const lineDiscount = Number(record.remise) || 0;
      const globalDiscountShare =
        totalHT > 0
          ? (lineBase / totalHT) *
            (Number(state.formData.remise_globale) || 0)
          : 0;
      const lineNet = lineBase - lineDiscount - globalDiscountShare;
      const lineTax = record.taxe
        ? filteredTaxes.find((tax: any) => tax.id === record.taxe)
        : null;
      const taxAmount = lineTax
        ? (lineNet * (Number(lineTax.rate) || 0)) / 100
        : 0;

      return {
        taxAmount,
        totalWithTax: lineNet + taxAmount,
      };
    },
    [filteredTaxes, state.formData.remise_globale, totalHT],
  );
  const updateLineQuantity = (record: any, index: number, newValue: any) => {
    const normalizedValue = normalizePositiveQuantity(newValue);
    const currentProduct = choices.products.find((product) => product.id === choiceItemId(record));
    const maxStock = resolveQuantityValue(currentProduct?.quantite_stock);

    if (
      requiresStrictStockCheck(currentProduct || record) &&
      maxStock !== undefined &&
      normalizedValue > maxStock
    ) {
      openStockShortageActions([
        {
          ...record,
          product_id: record.product_id || record.produit_id,
          product_name: record.nom_produit,
          required_quantity: normalizedValue,
          available_quantity: maxStock,
        },
      ]);
      return;
    }

    const updatedProducts = [...state.formData.produitsVendus];
    updatedProducts[index].quantite = normalizedValue;
    updatedProducts[index].quantity = normalizedValue;
    dispatch({
      type: "UPDATE_FORM_DATA",
      payload: {
        ...state.formData,
        produitsVendus: updatedProducts,
      },
    });
  };
  const immediatePaymentEnabled = Boolean(
    Form.useWatch("add_initial_payment", form),
  );
  const initialPaymentChoices = usePaymentFormChoices({ ...choices.scope, paymentType: "in", includeEntities: false,
    active: visible && !isEditing && immediatePaymentEnabled, enabled: canCreateInitialPayment });
  const termsValue = Form.useWatch("termes_conditions", form);
  const remarksValue = Form.useWatch("remarques", form);
  const invoiceSubjectValue = Form.useWatch("invoice_subject", form);
  const initialInvoiceSubject = state.formData.id ? normalizeInvoiceSubject(state.formData.invoice_subject) : null;
  const invoiceSubjectAllowed = isInvoiceSubjectEnabled(choices.references);
  const currentInvoiceSubject = normalizeInvoiceSubject(invoiceSubjectValue ?? state.formData.invoice_subject);
  const hasInvoiceSubject = Boolean(currentInvoiceSubject);
  const invoiceSubjectChanged = currentInvoiceSubject !== initialInvoiceSubject;
  const invoiceSubjectConflict = Boolean(choices.references && !invoiceSubjectAllowed && invoiceSubjectChanged);
  const additionalInformationSummary = useMemo(() => {
    const completedFields = [
      String(termsValue || "").trim() ? "Conditions renseignées" : null,
      String(remarksValue || "").trim() ? "Remarque ajoutée" : null,
    ].filter(Boolean);

    return completedFields.length > 0
      ? completedFields.join(" · ")
      : "Termes, conditions et remarques";
  }, [remarksValue, termsValue]);
  const warehouseContextLabel = useMemo(() => {
    const explicitWarehouseName = String(warehouseName || "").trim();
    if (explicitWarehouseName) return explicitWarehouseName;

    if (selectedWarehouse && typeof selectedWarehouse === "object") {
      const warehouse = selectedWarehouse as any;
      const objectWarehouseName = String(
        warehouse.name ||
          warehouse.warehouse_name ||
          warehouse.label ||
          "",
      ).trim();
      if (objectWarehouseName) return objectWarehouseName;
    }

    return Number.isFinite(selectedWarehouseId) && selectedWarehouseId > 0
      ? `Établissement #${selectedWarehouseId}`
      : "Aucun établissement sélectionné";
  }, [selectedWarehouse, selectedWarehouseId, warehouseName]);
  const submitLabel = state.formData.id
    ? "Mettre à jour la vente"
    : immediatePaymentEnabled
      ? "Enregistrer et encaisser"
      : "Enregistrer la vente";

  const onFinish = async (values: any) => {
    values = { ...form.getFieldsValue(true), ...values };
    if (submittingRef.current || !contextMatches) return;
    // Permission check before submitting
    if (!hasRequiredPermission) {
      message.error(
        "Vous n'avez pas la permission d'ajouter ou de modifier une vente.",
      );
      return;
    }

    // Vérification des données requises
    if (!selectedWarehouse) {
      message.error("Veuillez sélectionner un établissement");
      return;
    }

    // Extraire l'ID du client et l'objet client complet (pattern identique à GestionAchat)
    let clientId: number | string | null = null;

    // Traiter les différents cas possibles pour Client_ID
    if (values.Client_ID) {
      // Cas 1: Client_ID est un objet avec une propriété value (comme dans le Select avec labelInValue)
      if (
        typeof values.Client_ID === "object" &&
        values.Client_ID !== null &&
        "value" in values.Client_ID
      ) {
        clientId = values.Client_ID.value;
      }
      // Cas 2: Client_ID est une valeur directe (nombre ou chaîne)
      else if (
        typeof values.Client_ID === "number" ||
        typeof values.Client_ID === "string"
      ) {
        clientId = values.Client_ID;
      }
    }

    // Si nous n'avons pas trouvé d'ID, essayer de le récupérer depuis state.formData
    if (!clientId && state.formData.Client_ID) {
      // Traiter les différents cas possibles pour state.formData.Client_ID
      if (
        typeof state.formData.Client_ID === "object" &&
        state.formData.Client_ID !== null &&
        "value" in state.formData.Client_ID
      ) {
        // Si c'est un objet avec une propriété value
        clientId = (state.formData.Client_ID as any).value;
      } else if (
        typeof state.formData.Client_ID === "number" ||
        typeof state.formData.Client_ID === "string"
      ) {
        // Si c'est une valeur directe
        clientId = state.formData.Client_ID;
      }
    }

    if (!clientId) {
      message.error("Veuillez sélectionner un client valide");
      return;
    }

    if (state.formData.produitsVendus.length === 0) {
      message.error("Veuillez ajouter au moins un produit");
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const invoiceSubject = normalizeInvoiceSubject(values.invoice_subject);
      const subjectChanged = invoiceSubject !== initialInvoiceSubject;
      if (isOffline() && subjectChanged && !invoiceSubjectAllowed) {
        throw new Error("La saisie de l’objet n’est pas autorisée dans les réglages disponibles de cet établissement. Votre saisie est conservée.");
      }
      const currentProducts = isOffline() ? [] : await choices.verify(Number(clientId), state.formData.produitsVendus,
        { invoiceSubjectChanged: subjectChanged });
      if (!isEditing && immediatePaymentEnabled && !isOffline()) {
        const paymentChoices = await initialPaymentChoices.refresh({ force: true });
        if (!paymentChoices.paymentModes.some((mode) => Number(mode.id) === Number(values.initial_payment?.payment_mode_id)) ||
            !paymentChoices.treasuryAccounts.some((account) => Number(account.id) === Number(values.initial_payment?.treasury_account_id))) {
          throw new Error("Un choix du paiement initial n’est plus disponible. Votre saisie est conservée ; corrigez le mode ou le compte.");
        }
      }
    if (!state.formData.id && !isOffline()) {
      if (currentProducts.some((product) => product.item_type !== "service" && requiresStrictStockCheck(product) && product.quantite_stock === undefined)) {
        throw new Error("Le stock d’un article n’a pas pu être vérifié. Votre saisie est conservée ; réessayez.");
      }
      const shortages = buildMissingStockItems(
        state.formData.produitsVendus
          .filter(
            (product) =>
              product.item_type !== "service" &&
              requiresStrictStockCheck(currentProducts.find((choice) => choice.id === choiceItemId(product)) || product),
          )
          .map((product) => ({
            ...product,
            required_quantity: product.quantite,
            available_quantity: currentProducts.find((choice) => choice.id === choiceItemId(product))?.quantite_stock,
          })),
      );
      if (shortages.length > 0) {
        setStockShortageItems(shortages);
        return;
      }
    }

      const { totalHT: baseHT, totalRemises: totalRemisesSansFidelite } =
        calculerTotaux();

      const remiseGlobaleManuelle = Number(values.remise_globale) || 0;
      const offlineMode = isOffline();

      let loyaltyDiscount = state.formData.id
        ? Number((state.formData as any).loyalty_discount || 0)
        : 0;
      let loyaltyDiscountRate = state.formData.id
        ? Number((state.formData as any).loyalty_discount_rate || 0)
        : 0;
      let loyaltyDiscountApplied = state.formData.id
        ? Boolean((state.formData as any).loyalty_discount_applied)
        : false;

      if (
        !state.formData.id &&
        !offlineMode &&
        selectedCompany &&
        selectedWarehouse
      ) {
        try {
          const warehouseId = selectedWarehouse?.id ?? selectedWarehouse;
          const companyId = selectedCompany?.id ?? selectedCompany;
          const loyaltyEligibility = await api.fetchLoyaltyEligibility({
            warehouse_id: Number(warehouseId),
            user_id: Number(clientId),
            amount_ht: Number(baseHT) || 0,
            company_id: Number(companyId),
          });

          if (
            loyaltyEligibility?.eligible &&
            Number(loyaltyEligibility.discount_amount || 0) > 0
          ) {
            const ordersCount = Number(
              loyaltyEligibility.orders_count ??
                loyaltyEligibility.periods_count ??
                0,
            );
            const ordersRequired = Number(
              loyaltyEligibility.orders_required ??
                loyaltyEligibility.periods_required ??
                0,
            );
            let shouldApplyLoyalty = true;

            if (loyaltyEligibility.prompt_on_sale) {
              shouldApplyLoyalty = await new Promise<boolean>((resolve) => {
                Modal.confirm({
                  title: "Remise fidélité disponible",
                  content: (
                    <div>
                      <p>
                        Ce client est éligible à une remise fidélité de{" "}
                        <strong>
                          {Number(
                            loyaltyEligibility.discount_rate || 0,
                          ).toLocaleString()}
                          %
                        </strong>
                        .
                      </p>
                      <p>
                        Progression:{" "}
                        <strong>
                          {ordersCount.toLocaleString()} /{" "}
                          {ordersRequired.toLocaleString()}
                        </strong>{" "}
                        commandes sur la période.
                      </p>
                      <p>
                        Montant estimé:{" "}
                        <strong>
                          {Number(
                            loyaltyEligibility.discount_amount || 0,
                          ).toLocaleString()}{" "}
                          CFA
                        </strong>
                      </p>
                      <p>Appliquer la remise fidélité sur cette vente ?</p>
                    </div>
                  ),
                  okText: "Appliquer",
                  cancelText: "Ignorer",
                  onOk: () => resolve(true),
                  onCancel: () => resolve(false),
                });
              });
            }

            if (shouldApplyLoyalty) {
              loyaltyDiscountApplied = true;
              loyaltyDiscountRate = Math.max(
                0,
                Number(loyaltyEligibility.discount_rate || 0),
              );
              loyaltyDiscount = Math.max(
                0,
                Number(loyaltyEligibility.discount_amount || 0),
              );
            } else {
              loyaltyDiscountApplied = false;
              loyaltyDiscountRate = 0;
              loyaltyDiscount = 0;
            }
          }
        } catch (loyaltyError) {
          console.warn(
            "[VenteForm] Impossible de vérifier la fidélité pour cette vente:",
            loyaltyError,
          );
        }
      }

      const effectiveLoyaltyDiscount = loyaltyDiscountApplied
        ? loyaltyDiscount
        : 0;
      const totalRemisesCalc =
        totalRemisesSansFidelite + effectiveLoyaltyDiscount;
      const montantNetHT = Math.max(0, baseHT - totalRemisesCalc);

      const mappedProduitsVendus = state.formData.produitsVendus.map((p) => {
        let montant_taxe = 0;
        if (p.taxe) {
          const tax = filteredTaxes.find((t: any) => t.id === p.taxe);
          if (tax) {
            const produitBaseHT =
              (Number(p.prix_unitaire_HT) || 0) * (Number(p.quantite) || 0);
            const produitRemise = Number(p.remise) || 0;

            const proportionRemiseGlobale =
              baseHT > 0 ? (produitBaseHT / baseHT) * remiseGlobaleManuelle : 0;
            const proportionRemiseLoyalty =
              baseHT > 0
                ? (produitBaseHT / baseHT) * effectiveLoyaltyDiscount
                : 0;

            const produitNetHT = Math.max(
              0,
              produitBaseHT -
                produitRemise -
                proportionRemiseGlobale -
                proportionRemiseLoyalty,
            );

            const taxeRate = Number(tax.rate) || 0;
            montant_taxe = (produitNetHT * taxeRate) / 100;
          }
        }

        return {
          ...p,
          taxe: p.taxe ? Number(p.taxe) : null,
          montant_taxe: Number(montant_taxe) || 0,
          prix_unitaire_HT: Number(p.prix_unitaire_HT) || 0,
          quantite: normalizePositiveQuantity(p.quantite, 0),
          remise: Number(p.remise || 0),
          unit_id: p.unit_id ?? p.unit?.id ?? null,
          unit_name: p.unit_name || p.unit?.name,
          unit_short_name: p.unit_short_name || p.unit?.short_name,
        };
      });

      const totalTax = mappedProduitsVendus.reduce(
        (sum, p) => sum + (Number((p as any).montant_taxe) || 0),
        0,
      );
      const montantTTC = montantNetHT + totalTax;

      const initialPayment = state.formData.id
        ? undefined
        : buildInitialPaymentPayload(values, {
            paymentType: "in",
            defaultNotes: "Paiement enregistré pendant la facturation de la vente",
          });
      if (initialPayment && offlineMode) {
        message.error(
          "Le paiement immédiat nécessite une connexion. Désactivez-le pour enregistrer la vente hors ligne.",
        );
        return;
      }
      if (initialPayment && initialPayment.amount > montantTTC) {
        throw new Error("Le paiement initial dépasse le total actualisé. Votre saisie est conservée ; corrigez le montant.");
      }

      const staffUserId =
        !state.formData.id && typedAuth?.user?.id
          ? typedAuth.user.id
          : undefined;

      const venteData = {
        // Ajout de warehouse_id qui est obligatoire pour l'API
        warehouse_id: selectedWarehouseId,
        // Ajout de company_id récupéré du contexte
        company_id: selectedCompanyId,
        Date_Facture: values.Date_Facture.format("YYYY-MM-DD"),
        Statut_Vente: values.Statut_Vente,
        Client_ID: clientId,
        Montant_Total_HT: baseHT, // Base HT initiale
        Montant_Net_HT: montantNetHT, // Nouveau champ : Montant net HT après remises
        Montant_TTC: montantTTC, // Total TTC
        Montant_Remise: totalRemisesCalc, // Total des remises
        Montant_Taxe: totalTax, // Total des taxes
        remise_globale: remiseGlobaleManuelle,
        manual_discount: remiseGlobaleManuelle,
        loyalty_discount: effectiveLoyaltyDiscount,
        loyalty_discount_rate: loyaltyDiscountApplied ? loyaltyDiscountRate : 0,
        loyalty_discount_applied: loyaltyDiscountApplied,
        termes_conditions: values.termes_conditions || "",
        remarques: values.remarques || "",
        invoice_subject: invoiceSubject,
        produitsVendus: mappedProduitsVendus,
        // Statut de paiement initial
        payment_status: "Non payé",
        initial_payment: initialPayment,
        manual_invoice_number: state.formData.id ? undefined : await manualInvoiceNumber.prepare(),
        ...(staffUserId ? { staff_user_id: staffUserId } : {}),
      };
      choices.assertContext();
      if (state.formData.id) {
        // Permission check specifically for editing
        if (!canEdit) {
          message.error("Vous n'avez pas la permission de modifier une vente.");
          return;
        }
        await api.updateVente(state.formData.id, venteData);
        if (offlineMode) {
          message.info(
            "Vente mise à jour localement (en attente de synchronisation)",
          );
        } else {
          message.success("Vente mise à jour avec succès");
        }
      } else {
        // Permission check specifically for creating
        if (!canCreate) {
          message.error("Vous n'avez pas la permission d'ajouter une vente.");
          return;
        }
        await api.createVente(venteData);
        if (offlineMode) {
          message.info(
            "Vente enregistrée localement (en attente de synchronisation)",
          );
        } else {
          message.success("Vente ajoutée avec succès");
        }
      }

      if (!offlineMode) void choices.invalidate();
      form.resetFields();
      dispatch({
        type: "SET_FORM_DATA",
        payload: {
          ...initialState.formData,
          Date_Facture: dayjs().format("YYYY-MM-DD"),
          Statut_Vente: "Completé",
          Client_ID: "",
          remise_globale: 0,
          produitsVendus: [],
        },
      });
      if (restoredDraftMeta) {
        clearRestoredSalesDraft();
      }
      onClose();
      refreshSales();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement de la vente:", error);
      const serverStockItems = (error as any)?.stock_items;
      if (Array.isArray(serverStockItems) && serverStockItems.length > 0) {
        setStockShortageItems(serverStockItems);
        return;
      }
      message.error(
        error instanceof Error && error.message
          ? error.message
          : "Erreur lors de l'enregistrement de la vente",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleProductSelect = (product: Product) => {
    // Permission check before adding product
    if (!hasRequiredPermission) {
      message.error(
        "Vous n'avez pas la permission d'ajouter ou de modifier des produits.",
      );
      return;
    }

    const strictStockCheck = requiresStrictStockCheck(product);
    const availableStock = resolveQuantityValue(product.quantite_stock);
    if (
      strictStockCheck &&
      (availableStock === undefined || availableStock < MIN_SALE_QUANTITY)
    ) {
      if (availableStock !== undefined) {
        openStockShortageActions([
          {
            ...product,
            product_id: product.id_produit,
            product_name: product.nom_produit,
            required_quantity: 1,
            available_quantity: availableStock,
          },
        ]);
      } else {
        message.error(
          "Le stock de cet article est indisponible. Actualisez les produits avant de continuer.",
        );
      }
      return;
    }

    // Sauvegarde du client actuellement sélectionné dans le formulaire
    const currentClientId = form.getFieldValue("Client_ID");

    // Vérifier si le produit existe déjà dans le panier
    const existingProductIndex = state.formData.produitsVendus.findIndex(
      (p) => p.produit_id === product.id_produit,
    );

    if (existingProductIndex !== -1) {
      // Le produit existe déjà, incrémenter sa quantité
      const updatedProduitsVendus = [...state.formData.produitsVendus];

      // Vérifier si l'augmentation de quantité ne dépasse pas le stock disponible
      const currentQuantity =
        updatedProduitsVendus[existingProductIndex].quantite;

      const nextQuantity = normalizePositiveQuantity(currentQuantity + 1);
      if (
        strictStockCheck &&
        availableStock !== undefined &&
        nextQuantity > availableStock
      ) {
        openStockShortageActions([
          {
            ...product,
            product_id: product.id_produit,
            product_name: product.nom_produit,
            required_quantity: nextQuantity,
            available_quantity: availableStock,
          },
        ]);
        return;
      }

      // Incrémenter la quantité et mettre à jour les champs associés
      updatedProduitsVendus[existingProductIndex].quantite = nextQuantity;
      updatedProduitsVendus[existingProductIndex].quantity = nextQuantity;

      dispatch({
        type: "UPDATE_FORM_DATA",
        payload: {
          id: state.formData.id,
          Date_Facture: state.formData.Date_Facture,
          Statut_Vente: state.formData.Statut_Vente,
          Client_ID: currentClientId || state.formData.Client_ID, // Utiliser la valeur courante du formulaire
          remise_globale: state.formData.remise_globale,
          termes_conditions: state.formData.termes_conditions,
          remarques: state.formData.remarques,
          invoice_subject: state.formData.invoice_subject,
          produitsVendus: updatedProduitsVendus,
        },
      });

      // Réappliquer la valeur client_ID après le dispatch pour s'assurer qu'elle n'est pas perdue
      setTimeout(() => {
        if (currentClientId) {
          form.setFieldValue("Client_ID", currentClientId);
        }
      }, 0);

      // Incrémenter la clé pour reset le champ de recherche
      setSearchKey((prev) => prev + 1);
      void playBusinessAudioAlert("pos_cart_add");
      message.success(
        `Quantité de ${product.nom_produit} augmentée à ${currentQuantity + 1}`,
      );
    } else {
      // Le produit n'existe pas encore, l'ajouter au panier
      // Créer un nouvel objet produitVendu avec les champs mappés correctement
      const initialQuantity =
        strictStockCheck && availableStock !== undefined
          ? Math.min(1, availableStock)
          : 1;
      const normalizedInitialQuantity =
        normalizePositiveQuantity(initialQuantity);
      const produitVendu: ProduitVendu = {
        produit_id: product.id_produit,
        item_type: product.item_type || "product",
        service_id: product.service_id ?? null,
        nom_produit: product.nom_produit,
        quantite: normalizedInitialQuantity,
        prix_unitaire_HT: product.prix_vente,
        remise: 0,
        taxe: null,
        montant_taxe: 0,
        quantite_stock: availableStock,
        stock_management_mode: product.stock_management_mode,
        product_type: product.product_type,
        production_kind: product.production_kind,
        sellable_without_production: Boolean(
          product.sellable_without_production,
        ),
        image: product.image,
        // Ajouter les champs compatibles avec l'API
        product_id:
          product.item_type === "service" ? undefined : product.id_produit,
        product_name: product.nom_produit,
        quantity: normalizedInitialQuantity,
        unit_price: product.prix_vente,
        discount_rate: 0,
        tax_id: undefined,
        total_tax: 0,
        subtotal: product.prix_vente * normalizedInitialQuantity,
        ...resolveUnitMetadata(product),
      };

      dispatch({
        type: "UPDATE_FORM_DATA",
        payload: {
          id: state.formData.id,
          Date_Facture: state.formData.Date_Facture,
          Statut_Vente: state.formData.Statut_Vente,
          Client_ID: currentClientId || state.formData.Client_ID, // Utiliser la valeur courante du formulaire
          remise_globale: state.formData.remise_globale,
          termes_conditions: state.formData.termes_conditions,
          remarques: state.formData.remarques,
          invoice_subject: state.formData.invoice_subject,
          produitsVendus: [...state.formData.produitsVendus, produitVendu],
        },
      });

      // Réappliquer la valeur client_ID après le dispatch pour s'assurer qu'elle n'est pas perdue
      setTimeout(() => {
        if (currentClientId) {
          form.setFieldValue("Client_ID", currentClientId);
        }
      }, 0);

      // Incrémenter la clé pour reset le champ de recherche
      setSearchKey((prev) => prev + 1);
      void playBusinessAudioAlert("pos_cart_add");
      message.success(`${product.nom_produit} ajouté au panier`);
    }
  };

  // Disable form fields if user lacks permission
  const formDisabled = !hasRequiredPermission || !contextMatches;

  return (
    <>
      <Modal
        className="sale-form-modal"
        visible={visible}
        title={
          <div className="sale-form-modal__title">
            <div className="sale-form-modal__title-main">
              <ShoppingCartOutlined aria-hidden="true" />
              <div>
                <strong>
                  {state.formData.id ? "Modifier la vente" : "Nouvelle vente"}
                </strong>
                <span>
                  {state.formData.id
                    ? "Mettre à jour les articles et les informations de la vente"
                    : "Sélectionner un client, ajouter les articles et enregistrer la vente"}
                </span>
              </div>
            </div>
            <span
              className="sale-form-modal__context"
              title={warehouseContextLabel}
            >
              <ShopOutlined aria-hidden="true" />
              <span className="sale-form-modal__context-label">
                {warehouseContextLabel}
              </span>
            </span>
          </div>
        }
        width={window.innerWidth > 768 ? "96%" : "100%"}
        style={{ maxWidth: "1760px" }}
        bodyStyle={{
          maxHeight: "calc(100vh - 108px)",
          overflowY: "auto",
          padding: 0,
        }}
        onCancel={handleClose}
        footer={null}
      >
      <div className="sale-form-modal__content">
      {restoredDraftMeta && (
        <Alert
          type={restoredSaleShortages.length > 0 ? "warning" : "success"}
          showIcon
          message={
            restoredSaleShortages.length > 0
              ? "Panier restauré — stock encore insuffisant"
              : "Panier restauré — stock désormais suffisant"
          }
          description={
            "Les produits, prix et stocks ont été revalidés. Les données de paiement immédiat n'ont pas été restaurées" +
            (restoredDraftDetails.length > 0
              ? ". " + restoredDraftDetails.join(" ; ")
              : "") +
            "."
          }
          action={
            restoredSaleShortages.length > 0 ? (
              <Button
                size="small"
                onClick={() => openStockShortageActions(restoredSaleShortages)}
              >
                Voir les manquants
              </Button>
            ) : null
          }
          style={{ marginBottom: 12 }}
        />
      )}
      {/* Disable the entire form if the user doesn't have the required permission */}
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        preserve={true}
        disabled={formDisabled || submitting}
        validateTrigger={["onSubmit"]}
        className="sale-form"
        onValuesChange={(changedValues, allValues) => {
          // Ne plus gérer Client_ID ici car c'est maintenant géré par le onChange explicite du Select
          // Gérer d'autres champs si nécessaire
        }}
      >
        <div className="sale-form__layout">
          <main className="sale-form__work">
        <BusinessResourceNotice resource={choices.notice} label="les choix de la vente" />
        {(choices.missingEntity || choices.missingProducts.length > 0 || choices.missingTaxes.length > 0) && <Alert type="warning" showIcon
          style={{ marginBottom: 12 }} message="Une référence sélectionnée n’est plus disponible."
          description="Votre saisie est conservée. Choisissez un client disponible ou corrigez les articles concernés avant d’enregistrer." />}
        <Row gutter={[12, 8]} className="sale-form__setup">
          <Col xs={24} md={12}>
            <Form.Item label="Client" required>
              <div className="sale-form-client-control">
                <Form.Item
                  name="Client_ID"
                  noStyle
                  rules={[
                    { required: true, message: "Sélectionnez un client" },
                  ]}
                >
                  <Select
                    placeholder="Sélectionner un client" /* disabled is inherited */
                    showSearch
                    labelInValue
                    filterOption={false}
                    onSearch={choices.setSearch}
                    loading={choices.entityResource.loading}
                    dropdownRender={(menu) => <>{menu}<Space style={{ padding: 8 }}>
                      {choices.page > 1 && <Button size="small" onMouseDown={(event) => event.preventDefault()}
                        onClick={() => choices.setPage(choices.page - 1)}>Précédents</Button>}
                      {choices.hasMore && <Button size="small" onMouseDown={(event) => event.preventDefault()}
                        onClick={() => choices.setPage(choices.page + 1)}>Suivants</Button>}
                    </Space></>}
                    onChange={(selected: any) => {
                      // selected is an object: { value, label }
                      if (!selected) {
                        return;
                      }
                      const clientId = Number(selected.value);
                      if (isNaN(clientId) || clientId <= 0) {
                        console.error("ID de client invalide:", selected.value);
                        message.error("ID de client invalide");
                        return;
                      }

                      // Trouver le client complet dans la liste
                      const selectedClient = filteredClients.find(
                        (c: any) => c.id === clientId,
                      );

                      // Utiliser le nom du client trouvé comme étiquette si disponible
                      const clientName = selectedClient
                        ? selectedClient.name ||
                          selectedClient.company_name ||
                          selectedClient.Nom_Raison_Sociale ||
                          selected.label
                        : selected.label;

                      form.setFieldsValue({
                        Client_ID: {
                          value: clientId,
                          label: clientName,
                        },
                      });

                      // Mettre à jour state.formData avec l'ID du client
                      dispatch({
                        type: "UPDATE_FORM_DATA",
                        payload: {
                          ...state.formData,
                          Client_ID: String(clientId),
                        },
                      });
                    }}
                  >
                    {filteredClients.map((client: any) => (
                      <Option
                        key={client.id}
                        value={client.id}
                        searchLabel={buildCommercialEntitySearchText(client)}
                      >
                        {getCommercialEntityDisplayName(
                          client,
                          "Client inconnu",
                        )}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Button
                  className="sale-form-client-control__create"
                  type="default"
                  icon={<PlusOutlined />}
                  onClick={() => setQuickClientModalOpen(true)}
                  title={
                    canCreateClient
                      ? "Créer un nouveau client"
                      : "Permission de création des clients requise"
                  }
                  disabled={!canCreateClient || formDisabled}
                >
                  Nouveau client
                </Button>
              </div>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="Date_Facture"
              label="Date"
              rules={[{ required: true, message: "Sélectionnez une date" }]}
            >
              <DatePicker
                format="DD/MM/YYYY"
                allowClear={false}
                style={{ width: "100%" }} /* disabled is inherited */
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Form.Item
              name="Statut_Vente"
              label="Statut"
              rules={[{ required: true, message: "Sélectionnez un statut" }]}
            >
              <Select /* disabled is inherited */>
                <Option value="Commandé">Commandé</Option>
                <Option value="En traitement">En traitement</Option>
                <Option value="Expédié">Expédié</Option>
                <Option value="Livré">Livré</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {(invoiceSubjectAllowed || hasInvoiceSubject) && <>
          {invoiceSubjectConflict && <Alert type="warning" showIcon style={{ marginBottom: 12 }}
            message="La saisie de l’objet a été désactivée pour cet établissement."
            description="Votre texte est conservé. Réactivez l’option dans les paramètres de l’établissement ou rétablissez l’objet initial avant d’enregistrer."
            action={<Button size="small" onClick={() => form.setFieldValue("invoice_subject", initialInvoiceSubject || "")}>
              {initialInvoiceSubject ? "Rétablir l’objet enregistré" : "Retirer l’objet"}
            </Button>} />}
          <Form.Item name="invoice_subject" label="Objet de la facture"
            extra={!invoiceSubjectAllowed && !invoiceSubjectConflict
              ? "Objet enregistré conservé. La saisie est désactivée pour cet établissement."
              : "Facultatif. Imprimé sous les coordonnées du client."}
            rules={[{ max: INVOICE_SUBJECT_MAX_LENGTH, message: `L’objet ne doit pas dépasser ${INVOICE_SUBJECT_MAX_LENGTH} caractères.` }]}>
            <Input maxLength={INVOICE_SUBJECT_MAX_LENGTH} showCount readOnly={!invoiceSubjectAllowed}
              placeholder="Ex. : Fourniture de matelas pour l’équipement des chambres" />
          </Form.Item>
        </>}
        <ManualInvoiceNumberField control={manualInvoiceNumber} />
        <Row gutter={[12, 8]} className="sale-form__composer">
          <Col xs={24} md={18}>
            <Form.Item label="Ajouter un article ou un service">
              <div className="sale-form-search">
                {/* ProductSearch component needs to inherit disabled state or check internally */}
                {/* Assuming ProductSearch respects inherited 'disabled' or needs modification */}
                <OrderProductSearch
                  key={searchKey}
                  orderType="sales" action={isEditing ? "edit" : "create"} active={visible}
                  disabled={formDisabled || submitting}
                  onSelect={(product: Product) => handleProductSelect(product)}
                  // Supprimons la prop disabled qui cause l'erreur
                  // disabled={formDisabled}
                />
                {/* Affichons un message ou overlay si le form est désactivé */}
                {formDisabled && (
                  <div
                    className="sale-form-search__disabled"
                    aria-hidden="true"
                  />
                )}
              </div>
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label=" ">
              {/* Barcode scan button */}
              <Button
                className="sale-form__scanner"
                block
                icon={<QrcodeOutlined />}
                onClick={() => {
                  // Permission check before opening scanner modal
                  if (!hasRequiredPermission) {
                    message.error(
                      "Vous n'avez pas la permission d'ajouter des produits.",
                    );
                    return;
                  }
                  Modal.info({
                    title: "Scanner un code-barres",
                    width: 640,
                    maskClosable: true,
                    content: (
                      <div style={{ marginTop: 20 }}>
                        <BarcodeScanner
                          onScan={async (barcode) => {
                            // Permission check inside scanner callback as well
                            if (!hasRequiredPermission) {
                              message.error(
                                "Permission refusée pendant le scan.",
                              );
                              Modal.destroyAll();
                              return;
                            }
                            try {
                              const result = await fetchOrderFormOptions(choices.scope, "sales", isEditing ? "edit" : "create",
                                { kind: "products", barcode });
                              const response = { data: result.products };
                              choices.assertContext();

                              if (response.data && response.data.length > 0) {
                                const product = response.data[0];
                                handleProductSelect(product);
                                Modal.destroyAll(); // Fermer la modal
                              } else {
                                message.warning(
                                  "Aucun produit trouvé avec ce code-barres.",
                                );
                              }
                            } catch (error) {
                              console.error(
                                "Erreur lors de la recherche par code-barres:",
                                error,
                              );
                              message.error(
                                "Erreur lors de la recherche du produit.",
                              );
                            }
                          }}
                        />
                        <p style={{ textAlign: "center", marginTop: 15 }}>
                          Placez le code-barres devant la caméra pour le scanner
                        </p>
                      </div>
                    ),
                    onOk: () => {},
                  });
                }}
                disabled={formDisabled} // Disable button if form is disabled
              >
                Scanner un code-barres
              </Button>
            </Form.Item>
          </Col>
        </Row>
        <div className="sale-form-table-shell">
          <Table
            className="sale-form-table"
            dataSource={state.formData.produitsVendus}
            rowKey={(record: any) => record.produit_id.toString()}
            pagination={false}
            size="small"
            scroll={{ x: 1100 }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span className="sale-form-table__empty">
                      <strong>Aucun article ajouté</strong>
                      <small>
                        Recherchez un produit ou scannez son code-barres.
                      </small>
                    </span>
                  }
                />
              ),
            }}
          >
            <Table.Column
              title="Article / Service"
              dataIndex="nom_produit"
              width={250}
              fixed="left"
              render={(value: any, record: any) => (
                <div className="sale-form-item">
                  <SaleItemThumbnail
                    imagePath={record.image}
                    itemName={value}
                  />
                  <div className="sale-form-item__identity">
                    <strong title={value}>{value}</strong>
                    {record.item_type === "service" ? (
                      <span className="sale-form-item__meta sale-form-item__meta--service">
                        Service
                      </span>
                    ) : catalogStock(record) !== undefined ? (
                      <span className="sale-form-item__meta">
                        {normalizeStockManagementMode(
                          record.stock_management_mode,
                        ) === "unlimited"
                          ? "Disponibilité illimitée"
                          : normalizeStockManagementMode(
                                record.stock_management_mode,
                              ) === "pre_order"
                            ? `Sur commande · stock ${formatQuantity(catalogStock(record))}`
                            : `Stock disponible : ${formatQuantity(catalogStock(record))}`}
                      </span>
                    ) : null}
                  </div>
                </div>
              )}
            />
            <Table.Column
              title="Prix unitaire HT"
              dataIndex="prix_unitaire_HT"
              width={160}
              render={(value: any, record: any, index: number) =>
                editingLineIndex === index ? (
                  <InputNumber
                    min={0}
                    value={value}
                    addonAfter="CFA"
                    style={{ width: "100%" }}
                    formatter={(val: any) =>
                      val !== undefined && val !== null
                        ? formatNumber(Number(val))
                        : ""
                    }
                    parser={(val: any) =>
                      val ? val.replace(/\D/g, "") : ""
                    }
                    onChange={(newValue: any) => {
                      const updatedProducts = [
                        ...state.formData.produitsVendus,
                      ];
                      updatedProducts[index].prix_unitaire_HT = newValue || 0;
                      dispatch({
                        type: "UPDATE_FORM_DATA",
                        payload: {
                          ...state.formData,
                          produitsVendus: updatedProducts,
                        },
                      });
                    }}
                    disabled={formDisabled}
                  />
                ) : (
                  <span className="sale-form-line-value">
                    {formatNumber(Number(value) || 0)} CFA
                  </span>
                )
              }
            />
            <Table.Column
              title="Quantité"
              dataIndex="quantite"
              width={150}
              render={(value: any, record: any, index: number) => {
                const normalizedValue = normalizePositiveQuantity(value);

                return (
                  <div className="sale-form-quantity-stepper">
                    <Button
                      aria-label={`Diminuer la quantité de ${record.nom_produit}`}
                      disabled={formDisabled || normalizedValue <= 1}
                      onClick={() =>
                        updateLineQuantity(record, index, normalizedValue - 1)
                      }
                    >
                      −
                    </Button>
                  <InputNumber
                    min={MIN_SALE_QUANTITY}
                    step={0.00001}
                    controls={false}
                    value={value}
                    aria-label={`Quantité de ${record.nom_produit}`}
                    formatter={(displayValue: any, info) =>
                      info.userTyping
                        ? info.input
                        : displayValue === undefined || displayValue === null
                          ? ""
                          : formatQuantity(displayValue)
                    }
                    parser={(val: any) =>
                      sanitizeDecimalInput(val, QUANTITY_DECIMAL_PLACES)
                    }
                    onChange={(newValue: any) =>
                      updateLineQuantity(record, index, newValue)
                    }
                    disabled={formDisabled}
                  />
                    <Button
                      aria-label={`Augmenter la quantité de ${record.nom_produit}`}
                      disabled={formDisabled}
                      onClick={() =>
                        updateLineQuantity(record, index, normalizedValue + 1)
                      }
                    >
                      +
                    </Button>
                  </div>
                );
              }}
            />
            <Table.Column
              title="Remise"
              dataIndex="remise"
              width={135}
              render={(value: any, record: any, index: number) =>
                editingLineIndex === index ? (
                  <InputNumber
                    min={0}
                    value={value || 0}
                    addonAfter="CFA"
                    style={{ width: "100%" }}
                    formatter={(val: any) =>
                      val !== undefined && val !== null
                        ? formatNumber(Number(val))
                        : ""
                    }
                    parser={(val: any) =>
                      val ? val.replace(/\D/g, "") : ""
                    }
                    onChange={(newValue: any) => {
                      const updatedProducts = [
                        ...state.formData.produitsVendus,
                      ];
                      updatedProducts[index].remise = newValue || 0;
                      dispatch({
                        type: "UPDATE_FORM_DATA",
                        payload: {
                          ...state.formData,
                          produitsVendus: updatedProducts,
                        },
                      });
                    }}
                    disabled={formDisabled}
                  />
                ) : (
                  <span className="sale-form-line-value">
                    {formatNumber(Number(value) || 0)} CFA
                  </span>
                )
              }
            />
            <Table.Column
              title="Taxe"
              dataIndex="taxe"
              width={170}
              render={(value: any, record: any, index: number) => {
                const { taxAmount } = calculateLineAmounts(record);

                return editingLineIndex === index ? (
                  <div className="sale-form-tax-cell">
                    <Select
                      value={value}
                      style={{ width: "100%" }}
                      onChange={(newValue: any) => {
                        const updatedProducts = [
                          ...state.formData.produitsVendus,
                        ];
                        updatedProducts[index].taxe = newValue;
                        dispatch({
                          type: "UPDATE_FORM_DATA",
                          payload: {
                            ...state.formData,
                            produitsVendus: updatedProducts,
                          },
                        });
                      }}
                      disabled={formDisabled}
                    >
                      <Option value={null}>Aucune</Option>
                      {filteredTaxes.map((tax: any) => (
                        <Option key={tax.id} value={tax.id}>
                          {tax.name} ({tax.rate}%)
                        </Option>
                      ))}
                    </Select>
                    <span>{formatNumber(taxAmount)} CFA de taxe</span>
                  </div>
                ) : (
                  <span className="sale-form-line-value">
                    {formatNumber(taxAmount)} CFA
                  </span>
                );
              }}
            />
            <Table.Column
              title="Total TTC"
              width={125}
              render={(_: any, record: any) => {
                const { totalWithTax } = calculateLineAmounts(record);
                return (
                  <strong className="sale-form-line-total">
                    {formatNumber(totalWithTax)} CFA
                  </strong>
                );
              }}
            />
            <Table.Column
              title="Actions"
              width={108}
              fixed="right"
              render={(_: any, record: any, index: number) => (
                <div className="sale-form-line-actions">
                  <Button
                    className="sale-form-line-action"
                    icon={
                      editingLineIndex === index ? (
                        <CheckOutlined />
                      ) : (
                        <EditOutlined />
                      )
                    }
                    aria-label={
                      editingLineIndex === index
                        ? `Terminer la modification de ${record.nom_produit}`
                        : `Modifier ${record.nom_produit}`
                    }
                    disabled={formDisabled}
                    onClick={() =>
                      setEditingLineIndex((currentIndex) =>
                        currentIndex === index ? null : index,
                      )
                    }
                  />
                  <Button
                    className="sale-form-line-action"
                    danger
                    icon={<DeleteOutlined />}
                    aria-label={`Retirer ${record.nom_produit} de la vente`}
                    disabled={formDisabled}
                    onClick={() => {
                      if (!hasRequiredPermission) return;
                      const updatedProducts =
                        state.formData.produitsVendus.filter(
                          (_: any, i: number) => i !== index,
                        );
                      setEditingLineIndex(null);
                      dispatch({
                        type: "UPDATE_FORM_DATA",
                        payload: {
                          ...state.formData,
                          produitsVendus: updatedProducts,
                        },
                      });
                    }}
                  />
                </div>
              )}
            />
          </Table>
        </div>
        <Collapse
          className="sale-form-additional"
          items={[
            {
              key: "additional-information",
              label: (
                <div className="sale-form-additional__label">
                  <span>
                    <FileTextOutlined aria-hidden="true" />
                    <strong>Informations complémentaires</strong>
                  </span>
                  <small>{additionalInformationSummary}</small>
                </div>
              ),
              children: (
                <Row gutter={[12, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="termes_conditions"
                      label="Termes et conditions"
                    >
                      <Input.TextArea rows={3} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="remarques" label="Remarques">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                  </Col>
                </Row>
              ),
            },
          ]}
        />
          </main>

          <aside className="sale-form-summary" aria-label="Résumé de la vente">
            <div className="sale-form-summary__scroll">
              <div className="sale-form-summary__heading">
                <BarChartOutlined aria-hidden="true" />
                <h3>Résumé de la vente</h3>
              </div>
              <div className="sale-form-summary__count">
                {state.formData.produitsVendus.length === 0
                  ? "Aucun article"
                  : `${state.formData.produitsVendus.length} article${
                      state.formData.produitsVendus.length > 1 ? "s" : ""
                    }`}
              </div>

              <div className="sale-form-summary__breakdown">
                <div>
                  <span>Base HT</span>
                  <strong>{formatNumber(totalHT)} CFA</strong>
                </div>
                <div>
                  <span>Remises</span>
                  <strong>{formatNumber(totalRemises)} CFA</strong>
                </div>
                {totalRemises > 0 && (
                  <div>
                    <span>Montant net HT</span>
                    <strong>{formatNumber(montantNetHT)} CFA</strong>
                  </div>
                )}
                <div>
                  <span>Taxes</span>
                  <strong>{formatNumber(totalTaxes)} CFA</strong>
                </div>
              </div>

              <div className="sale-form-summary__total">
                <span>Total TTC</span>
                <strong>{formatNumber(totalTTC)} CFA</strong>
              </div>

              {!state.formData.id ? (
                <OrderInitialPaymentFields
                  form={form}
                  total={totalTTC}
                  companyId={selectedCompanyId}
                  warehouseId={selectedWarehouseId}
                  canCreatePayment={canCreateInitialPayment}
                  disabled={formDisabled || submitting}
                  choiceResource={initialPaymentChoices}
                  presentation="summary"
                  inactiveTitle="Vente non payée"
                  description="Encaissement client partiel ou total, enregistré avec la vente."
                />
              ) : (
                <div className="sale-form-summary__payment-readonly">
                  <span>Statut du paiement</span>
                  <Tag
                    color={
                      state.formData.payment_status === "Payé"
                        ? "green"
                        : state.formData.payment_status === "Partiellement payé"
                          ? "orange"
                          : "red"
                    }
                  >
                    {state.formData.payment_status || "Non payé"}
                  </Tag>
                </div>
              )}
            </div>

            <div className="sale-form-summary__actions">
              <Button disabled={false} onClick={handleClose}>
                Annuler
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => form.submit()}
                loading={submitting}
                disabled={!hasRequiredPermission || !contextMatches || (!isOffline() && (!choices.ready || choices.missingEntity || choices.missingProducts.length > 0 || choices.missingTaxes.length > 0))}
              >
                {submitLabel}
              </Button>
            </div>
          </aside>
        </div>
      </Form>
      </div>

      {/* Modal de création rapide de client */}
      <QuickClientModal
        isOpen={quickClientModalOpen}
        onClose={() => setQuickClientModalOpen(false)}
        onSuccess={handleQuickClientSuccess}
        currentWarehouseId={selectedWarehouse?.id ?? selectedWarehouse}
      />
      </Modal>
      <StockShortageActionsModal
        open={stockShortageItems.length > 0}
        title="Approvisionner les produits manquants pour la vente"
        items={stockShortageItems}
        canPurchase={canCreatePurchase}
        canAdjust={canCreateStockAdjustment}
        canProduce={canCreateProduction}
        productionTerm={productionTerminology.costContext}
        productionActionLabel={`${productionTerminology.launchAction} — recommandé`}
        onCancel={() => setStockShortageItems([])}
        onPurchase={() => navigateToStockResolution("purchase")}
        onAdjustment={() => navigateToStockResolution("adjustment")}
        onProduction={() => navigateToStockResolution("production")}
      />
    </>
  );
};

interface VentesTableProps {
  refreshSales: () => Promise<void>;
  active?: boolean;
}

export const VentesTable: React.FC<VentesTableProps> = ({ refreshSales, active = true }) => {
  const invalidateSales = useBusinessMutationInvalidation("sale");
  const returnDocuments = useSalesReturnDocument();
  const context = useContext(VentesContext);
  if (!context) {
    throw new Error("VentesTable must be used within a VentesContext.Provider");
  }
  // Get auth from context
  const { state, dispatch, api, auth } = context;

  // Type l'objet auth correctement
  const typedAuth = auth as AuthContextType;

  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [detailVente, setDetailVente] = useState<any>(null);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [invoiceData, setInvoiceData] = useState<any>(null);
  const [warehouseData, setWarehouseData] = useState<any>(null);
  const [ticketVisible, setTicketVisible] = useState<boolean>(false);
  const [ticketData, setTicketData] = useState<any>(null);
  const [unitsById, setUnitsById] = useState<
    Record<number, { name?: string; short_name?: string }>
  >({});
  const { selectedWarehouse, selectedCompany } = useSelection();
  // Removed useAuth here as it's passed via context

  const resetLinkedDetail = useCallback(() => {
    setShowDetailModal(false);
    setDetailVente(null);
  }, []);
  const openLinkedDetail = useCallback((sale: any) => {
    setDetailVente(sale);
    setShowDetailModal(true);
  }, []);
  const { loading: linkedSaleLoading, clearLink: clearSaleLink } = useSaleDetailLink({
    api, active, onOpen: openLinkedDetail, resetDetail: resetLinkedDetail,
  });

  const buildScopedApiParams = useCallback(
    (source: any = {}) => {
      const companyId =
        source?.company_id ?? selectedCompany?.id ?? selectedCompany;
      const warehouseId =
        source?.warehouse_id ?? selectedWarehouse?.id ?? selectedWarehouse;

      const params: { company_id?: number; warehouse_id?: number } = {};
      if (companyId) {
        params.company_id = Number(companyId);
      }
      if (warehouseId) {
        params.warehouse_id = Number(warehouseId);
      }
      return params;
    },
    [selectedCompany, selectedWarehouse],
  );

  // Logging pour debugging
  useEffect(() => {}, [selectedWarehouse, selectedCompany]);

  useEffect(() => {
    const loadUnits = async () => {
      try {
        const selectedCompanyId =
          (selectedCompany as any)?.id ?? selectedCompany;
        const parsedCompanyId = selectedCompanyId
          ? Number(selectedCompanyId)
          : undefined;
        const companyId =
          parsedCompanyId && !Number.isNaN(parsedCompanyId)
            ? parsedCompanyId
            : undefined;

        const units = await api.fetchUnits(companyId);
        const nextUnitsById = (Array.isArray(units) ? units : []).reduce(
          (
            acc: Record<number, { name?: string; short_name?: string }>,
            unit: any,
          ) => {
            const unitId = Number(unit?.id);
            if (!Number.isNaN(unitId)) {
              acc[unitId] = {
                name: unit?.name,
                short_name: unit?.short_name,
              };
            }
            return acc;
          },
          {},
        );

        setUnitsById(nextUnitsById);
      } catch (error) {
        console.error(
          "Erreur lors du chargement des unités (table ventes):",
          error,
        );
        setUnitsById({});
      }
    };

    loadUnits();
  }, [api, selectedCompany]);

  const findProductById = useCallback(
    (productId: any) =>
      state.produits.find(
        (p: any) =>
          Number(p?.id_produit) === Number(productId) ||
          Number(p?.id) === Number(productId) ||
          Number(p?.product_id) === Number(productId),
      ),
    [state.produits],
  );

  const resolveProductName = useCallback(
    (productId: any): string => {
      const product = findProductById(productId);
      return (
        product?.nom_produit ||
        (product as any)?.name ||
        (product as any)?.product_name ||
        `Produit ${productId ?? "inconnu"}`
      );
    },
    [findProductById],
  );

  const loadVenteDetails = async (record: any) => {
    if (!record.produitsVendus) {
      try {
        setLoading(true);

        const scopedParams = buildScopedApiParams(record);
        const venteDetail = await api.fetchUnifiedSaleDetail(
          record,
          scopedParams,
        );
        const orderItems = isEcommerceSale(record)
          ? venteDetail.produitsVendus || []
          : await api.fetchOrderItems(record.id, scopedParams);

        const updatedRecord = { ...record, produitsVendus: orderItems };

        // Mettre à jour l'état avec les produits vendus
        dispatch({
          type: "UPDATE_VENTE_DETAIL",
          payload: { id: record.id, produitsVendus: orderItems },
        });

        return updatedRecord;
      } catch (error) {
        console.error(
          "Erreur lors du chargement des détails de la vente:",
          error,
        );
        message.error("Erreur lors du chargement des détails de la vente");
      } finally {
        setLoading(false);
      }
    }
    return record;
  };

  const loadVenteDetailsForModal = async (record: any) => {
    try {
      setLoading(true);

      // Extraire l'ID, que record soit un objet ou un ID directement
      const recordId = typeof record === "object" ? record.id : record;
      const saleRecord =
        typeof record === "object"
          ? record
          : { id: recordId, source_id: recordId, record_source: "internal" };

      // Récupérer les détails de la vente
      const scopedParams = buildScopedApiParams(record);
      const venteDetail = await api.fetchUnifiedSaleDetail(
        saleRecord,
        scopedParams,
      );

      // Si record n'est pas un objet, utiliser seulement les données de venteDetail
      const baseRecord = typeof record === "object" ? record : {};

      // Créer une copie de l'enregistrement avec les détails mis à jour
      const updatedRecord = {
        ...baseRecord,
        ...venteDetail,
        id: venteDetail.id ?? recordId, // S'assurer que l'ID est présent
        // S'assurer que les notes et termes_condition sont inclus
        notes:
          venteDetail.notes || (typeof record === "object" ? record.notes : ""),
        terms_condition:
          venteDetail.terms_condition ||
          (typeof record === "object" ? record.terms_condition : ""),
        // CORRECTION CRITIQUE: S'assurer que company_id et warehouse_id sont présents depuis venteDetail
        company_id:
          venteDetail.company_id ||
          (typeof record === "object" ? record.company_id : null),
        warehouse_id:
          venteDetail.warehouse_id ||
          (typeof record === "object" ? record.warehouse_id : null),
      };

      // Vérifier si les produitsVendus sont déjà inclus dans la réponse
      if (venteDetail.produitsVendus && venteDetail.produitsVendus.length > 0) {
        updatedRecord.produitsVendus = venteDetail.produitsVendus;
        setDetailVente(updatedRecord);
        return updatedRecord;
      }

      // Si les produitsVendus ne sont pas inclus, récupérer les éléments de la vente séparément
      const orderItems = isEcommerceSale(saleRecord)
        ? venteDetail.produitsVendus || []
        : await api.fetchOrderItems(recordId, scopedParams);

      if (!orderItems || orderItems.length === 0) {
        console.warn(
          "Aucun produit trouvé pour la vente ID (modal):",
          recordId,
        );

        // Utiliser invoice_number s'il est disponible dans l'objet
        const invoiceNumber =
          typeof record === "object" && record.invoice_number
            ? record.invoice_number
            : venteDetail.invoice_number || recordId;

        message.warning(`Aucun produit trouvé pour la vente #${invoiceNumber}`);
        updatedRecord.produitsVendus = [];
        setDetailVente(updatedRecord);
        return updatedRecord;
      }

      // Mettre à jour l'enregistrement avec les produits vendus
      updatedRecord.produitsVendus = orderItems;

      // Mettre à jour l'état du détail de la vente
      setDetailVente(updatedRecord);

      return updatedRecord;
    } catch (error) {
      console.error(
        "Erreur lors du chargement des détails de la vente pour le modal:",
        error,
      );
      // Ne pas afficher ce message d'erreur si on est en train de rafraîchir après un paiement
      if (typeof record !== "object" || !record.id) {
        message.error("Erreur lors du chargement des détails de la vente");
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  const loadVenteForEdit = async (record: any) => {
    // Permission check before loading data for edit
    if (!checkPermission(typedAuth, "Gestion Commerciale.Ventes.Ventes.edit")) {
      message.error("Vous n'avez pas la permission de modifier cette vente.");
      return;
    }
    try {
      setLoading(true);

      // Récupérer les détails de la vente
      const scopedParams = buildScopedApiParams(record);
      const venteData = await api.fetchVenteDetail(
        String(record.id),
        scopedParams,
      );

      // Vérifier si les produitsVendus sont déjà inclus dans la réponse
      let produitsVendus = [];

      if (venteData.produitsVendus && venteData.produitsVendus.length > 0) {
        // Mapper les éléments de la vente au format attendu par le formulaire
        produitsVendus = venteData.produitsVendus.map((item: any) => ({
          ...resolveUnitMetadata(item),
          produit_id:
            item.item_type === "service"
              ? -Number(item.service_id)
              : Number(item.product_id),
          item_type: item.item_type || "product",
          product_id: item.product_id ?? null,
          service_id: item.service_id ?? null,
          nom_produit:
            item.product_name ||
            state.produits.find(
              (p: Product) =>
                p.id_produit ===
                (item.item_type === "service"
                  ? -Number(item.service_id)
                  : Number(item.product_id)),
            )?.nom_produit ||
            (item.item_type === "service"
              ? "Service " + item.service_id
              : "Produit " + item.product_id),
          quantite: normalizePositiveQuantity(item.quantity, 0),
          prix_unitaire_HT: Number(item.unit_price) || 0,
          remise: Number(item.discount_rate) || 0,
          taxe: item.tax_id,
          montant_taxe: Number(item.total_tax) || 0,
          image:
            item.image ||
            item.image_url ||
            item.product_image ||
            state.produits.find(
              (product: Product) =>
                product.id_produit === Number(item.product_id),
            )?.image,
        }));
      } else {
        // Si les produitsVendus ne sont pas inclus, récupérer les éléments de la vente séparément
        const orderItems = await api.fetchOrderItems(record.id, scopedParams);

        if (orderItems && orderItems.length > 0) {
          // Mapper les éléments de la vente au format attendu par le formulaire
          produitsVendus = orderItems.map((item: any) => ({
            ...resolveUnitMetadata(item),
            produit_id:
              item.item_type === "service"
                ? -Number(item.service_id)
                : Number(item.product_id),
            item_type: item.item_type || "product",
            product_id: item.product_id ?? null,
            service_id: item.service_id ?? null,
            nom_produit:
              item.product_name ||
              state.produits.find(
                (p: Product) =>
                  p.id_produit ===
                  (item.item_type === "service"
                    ? -Number(item.service_id)
                    : Number(item.product_id)),
              )?.nom_produit ||
              (item.item_type === "service"
                ? "Service " + item.service_id
                : "Produit " + item.product_id),
            quantite: normalizePositiveQuantity(item.quantity, 0),
            prix_unitaire_HT: Number(item.unit_price) || 0,
            remise: Number(item.discount_rate) || 0,
            taxe: item.tax_id,
            montant_taxe: Number(item.total_tax) || 0,
            image:
              item.image ||
              item.image_url ||
              item.product_image ||
              state.produits.find(
                (product: Product) =>
                  product.id_produit === Number(item.product_id),
              )?.image,
          }));
        }
      }

      // Préparer les données du formulaire
      // Note: Client_ID est stocké comme string et sera converti en {value, label} par le useEffect de VenteForm
      const formattedData: VenteFormData = {
        id: venteData.id,
        Date_Facture: venteData.order_date || dayjs().format("YYYY-MM-DD"),
        Statut_Vente: venteData.order_status || "Commandé",
        Client_ID: String(venteData.user_id), // String sera converti en {value, label} par le useEffect
        remise_globale:
          Number(venteData.manual_discount ?? venteData.discount) || 0,
        manual_discount: Number(venteData.manual_discount ?? 0) || 0,
        loyalty_discount: Number(venteData.loyalty_discount ?? 0) || 0,
        loyalty_discount_rate:
          Number(venteData.loyalty_discount_rate ?? 0) || 0,
        loyalty_discount_applied:
          Number(venteData.loyalty_discount_applied) === 1 ||
          venteData.loyalty_discount_applied === true,
        termes_conditions: venteData.terms_condition || "",
        remarques: venteData.notes || "",
        invoice_subject: venteData.invoice_subject || "",
        produitsVendus: produitsVendus,
        // CORRECTION CRITIQUE: Charger TOUS les champs nécessaires
        paid_amount: parseFloat(venteData.paid_amount) || 0,
        payment_status: venteData.payment_status || "Non payé",
        invoice_type: venteData.invoice_type || "standard",
      };

      // Mettre à jour l'état avec les données du formulaire et ouvrir le formulaire
      dispatch({ type: "SET_FORM_DATA", payload: formattedData });
      dispatch({ type: "TOGGLE_FORM", payload: true });

      // Note: La valeur du champ Client sera définie par le useEffect dans VenteForm
      // qui détectera le changement de state.formData et convertira Client_ID en format {value, label}
    } catch (error) {
      console.error(
        "Erreur lors du chargement des détails de la vente pour modification:",
        error,
      );
      message.error("Erreur lors du chargement des détails de la vente");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadWarehouseInfo = async () => {
      if (!selectedWarehouse) {
        setWarehouseData(null);
        return;
      }

      setWarehouseData(null);
      try {
        const warehouse = await api.fetchWarehouse(selectedWarehouse);
        if (!cancelled) {
          setWarehouseData(warehouse || null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Erreur lors de la récupération de l'établissement:", error);
        }
      }
    };

    void loadWarehouseInfo();
    return () => {
      cancelled = true;
    };
  }, [api, selectedWarehouse]);

  // Ajouter handleTabChange qui était manquant
  const handleTabChange = (activeKey: string) => {
    dispatch({ type: "SET_ACTIVE_TAB", payload: activeKey });
  };

  // Define columns inside the component with access to auth from context
  const columns: TableProps<any>["columns"] = [
    {
      // Column: Selection Checkbox
      title: (
        <Checkbox
          checked={
            state.ventes.some((vente) => !isEcommerceSale(vente)) &&
            state.selectedVenteIds.length ===
              state.ventes.filter((vente) => !isEcommerceSale(vente)).length
          }
          indeterminate={
            state.selectedVenteIds.length > 0 &&
            state.selectedVenteIds.length <
              state.ventes.filter((vente) => !isEcommerceSale(vente)).length
          }
          onChange={(e) => {
            const allIds = state.ventes
              .filter((vente) => !isEcommerceSale(vente))
              .map((vente) => vente.id);
            dispatch({
              type: "SET_SELECTED_VENTE_IDS",
              payload: e.target.checked ? allIds : [],
            });
          }}
        />
      ),
      dataIndex: "selection",
      key: "selection",
      width: 42,
      render: (_: any, record: any) => {
        const externalSale = isEcommerceSale(record);
        return (
          <Tooltip
            title={
              externalSale
                ? "Les ventes web sont gérées depuis Commandes en ligne"
                : undefined
            }
          >
            <Checkbox
              disabled={externalSale}
              checked={state.selectedVenteIds.includes(record.id)}
              onChange={(e) => {
                dispatch({
                  type: e.target.checked
                    ? "ADD_SELECTED_VENTE_ID"
                    : "REMOVE_SELECTED_VENTE_ID",
                  payload: record.id,
                });
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      title: "N° facture",
      dataIndex: "invoice_number",
      key: "invoice_number",
      width: 145,
      render: (invoiceNumber: string) => (
        <span className="sales-list__invoice-number">{invoiceNumber}</span>
      ),
    },
    {
      title: "Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (text: string): string => dayjs(text).format("DD/MM/YYYY"),
      sorter: (a: any, b: any) => {
        const dateA = dayjs(a.order_date);
        const dateB = dayjs(b.order_date);
        return dateA.valueOf() - dateB.valueOf();
      },
      defaultSortOrder: "descend",
      width: 110,
    },
    {
      title: "Client",
      key: "client",
      render: (_: any, record: any): React.ReactNode => {
        if (record.entity_name || record.user_name) {
          return record.entity_name || record.user_name;
        }

        if (!record.user_id) {
          return "Non spécifié";
        }

        // Rechercher le client dans la liste des clients

        const client = state.clients.find(
          (c: any) => c.id === Number(record.user_id),
        );

        if (client) {
          return (
            client.name ||
            client.company_name ||
            client.Nom_Raison_Sociale ||
            "Client " + client.id
          );
        }

        return "Inconnu";
      },
      width: 175,
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      width: 130,
    },
    {
      title: "Canal",
      dataIndex: "sales_channel",
      key: "sales_channel",
      width: 145,
      render: (channel: string) => {
        const display = getSalesChannelDisplay(channel);
        return (
          <Tag color={display.color} icon={display.icon}>
            {display.label}
          </Tag>
        );
      },
    },
    {
      title: "Statut",
      dataIndex: "order_status",
      key: "order_status",
      render: (status: string, record: any) => {
        if (record.__offline) {
          return <Tag color="orange">En attente sync</Tag>;
        }
        let color = "green";
        if (status === "En attente") color = "orange";
        if (status === "Annulé") color = "red";
        return <Tag color={color}>{status}</Tag>;
      },
      width: 105,
    },
    {
      title: "Paiement",
      dataIndex: "payment_status",
      key: "payment_status",
      render: (status: string) => {
        let color = "";
        let displayText = status;

        if (status === "Non payé") {
          color = "red";
          displayText = "Non payé";
        } else if (status === "Partiel" || status === "Partiellement payé") {
          color = "orange";
          displayText = "Partiellement payé";
        } else if (status === "Payé") {
          color = "green";
          displayText = "Payé";
        }

        return <Tag color={color}>{displayText}</Tag>;
      },
      width: 130,
    },
    {
      title: "Livraison",
      dataIndex: "delivery_status",
      key: "delivery_status",
      width: 140,
      render: (_: string, record: any) => {
        if (record.__offline) {
          return <Tag color="default">Hors ligne</Tag>;
        }

        if (record.delivery_status === "delivered") {
          return (
            <Tooltip
              title={
                record.delivery_date
                  ? `Livrée le ${dayjs(record.delivery_date).format(
                      "DD/MM/YYYY HH:mm",
                    )}`
                  : "Livraison validée"
              }
            >
              <Tag color="green">{record.delivery_number || "Livrée"}</Tag>
            </Tooltip>
          );
        }

        if (record.delivery_status === "not_applicable") {
          return <Tag color="blue">Service · sans livraison</Tag>;
        }

        return <Tag color="orange">En attente</Tag>;
      },
    },
    {
      title: "Total TTC",
      dataIndex: "total",
      key: "total",
      align: "right", // Align numbers to the right
      render: (total: number) => `${formatNumber(total)} CFA`,
      width: 115,
    },
    {
      title: "Montant payé",
      dataIndex: "paid_amount",
      key: "paid_amount",
      align: "right", // Align numbers to the right
      render: (amount: number) => `${formatNumber(amount || 0)} CFA`,
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 58,
      render: (_: any, record: any) => {
        // Get permissions at the start of render
        const isOfflineRecord = record.__offline === true;
        const externalSale = isEcommerceSale(record);
        const canView = checkPermission(
          typedAuth,
          "Gestion Commerciale.Ventes.Ventes.view",
        );
        const canEdit = checkPermission(
          typedAuth,
          "Gestion Commerciale.Ventes.Ventes.edit",
        );
        const canDelete = checkPermission(
          typedAuth,
          "Gestion Commerciale.Ventes.Ventes.delete",
        );
        // Assume view permission is needed for download
        const canDownload = canView;
        const isDelivered = record.delivery_status === "delivered";

        // Disable edit if already paid or deleted
        const isEditDisabled =
          isOfflineRecord ||
          record.is_deleted === 1 ||
          record.paid_amount > 0 ||
          isDelivered;
        // Disable delete if already paid or not deletable flag is set
        const isDeleteDisabled =
          isOfflineRecord ||
          record.paid_amount > 0 ||
          record.is_deletable === 0 ||
          isDelivered;
        // Disable restore if not deleted
        const isRestoreDisabled =
          isOfflineRecord || record.is_deleted !== 1 || isDelivered;
        // Disable cancel if not paid > 0
        const isCancelDisabled =
          isOfflineRecord || !(record.paid_amount > 0) || isDelivered;

        const actionItems: any[] = [];

        if (canView) {
          actionItems.push({
            key: "view",
            icon: <EyeOutlined />,
            label: isOfflineRecord
              ? "Détail indisponible hors ligne"
              : "Voir le détail",
            disabled: isOfflineRecord,
            onClick: () => {
              setDetailVente(record);
              setShowDetailModal(true);
              loadVenteDetailsForModal(record);
            },
          });
        }

        if (canDownload && !externalSale) {
          actionItems.push(
            {
              key: "invoice-preview",
              icon: <FilePdfOutlined />,
              label: isOfflineRecord
                ? "Facture indisponible hors ligne"
                : "Ouvrir / imprimer la facture",
              disabled: isOfflineRecord,
              onClick: () => handleOpenInvoice(record),
            },
            {
              key: "receipt",
              icon: <PrinterOutlined />,
              label: isOfflineRecord
                ? "Ticket indisponible hors ligne"
                : "Réimprimer le ticket de caisse",
              disabled: isOfflineRecord,
              onClick: () => handleReprintReceipt(record),
            },
          );
        }

        if (returnDocuments.canPrint && !externalSale && !record.is_deleted) {
          const disabledReason = getSalesReturnDocumentDisabledReason(record, "summary");
          actionItems.push({
            key: "return-summary",
            icon: <PrinterOutlined />,
            label: "Imprimer le récapitulatif après retours",
            title: disabledReason,
            disabled: isOfflineRecord || returnDocuments.loading || Boolean(disabledReason),
            onClick: () => void returnDocuments.print(record, "summary"),
          });
        }

        if (canEdit && !externalSale) {
          const editLabel = isOfflineRecord
            ? "Modification indisponible hors ligne"
            : isDelivered
            ? "Vente livrée — modification indisponible"
            : record.is_deleted === 1
            ? "Vente supprimée — modification indisponible"
            : record.paid_amount > 0
            ? "Vente payée — modification indisponible"
            : "Modifier";
          actionItems.push({
            key: "edit",
            icon: <EditOutlined />,
            label: editLabel,
            disabled: isEditDisabled,
            onClick: () => loadVenteForEdit(record),
          });
        }

        if (canDelete && !externalSale) {
          if (actionItems.length > 0) actionItems.push({ type: "divider" });

          if (record.is_deleted === 1) {
            actionItems.push({
              key: "restore",
              icon: <RollbackOutlined />,
              label: isRestoreDisabled
                ? "Restauration indisponible"
                : "Restaurer",
              disabled: isRestoreDisabled,
              onClick: () =>
                Modal.confirm({
                  title: "Confirmation",
                  content: "Voulez-vous restaurer cette vente ?",
                  onOk: async () => {
                    if (!canDelete) return;
                    try {
                      await api.restoreVente(record.id);
                      void invalidateSales();
                      await refreshSales();
                      message.success("Vente restaurée avec succès");
                    } catch (error) {
                      message.error("Erreur lors de la restauration");
                    }
                  },
                }),
            });
          } else if (record.paid_amount > 0) {
            actionItems.push({
              key: "cancel",
              icon: <StopOutlined />,
              label: isCancelDisabled
                ? "Annulation indisponible"
                : "Annuler la vente",
              disabled: isCancelDisabled,
              danger: !isCancelDisabled,
              onClick: () =>
                Modal.confirm({
                  title: "Confirmation",
                  content:
                    "Cette vente contient des paiements. Voulez-vous l'annuler ?",
                  onOk: async () => {
                    if (!canDelete) return;
                    try {
                      await api.cancelVente(record.id);
                      void invalidateSales();
                      await refreshSales();
                      message.success("Vente annulée avec succès");
                    } catch (error) {
                      message.error("Erreur lors de l'annulation");
                    }
                  },
                }),
            });
          } else {
            actionItems.push({
              key: "delete",
              icon: <DeleteOutlined />,
              label: isDeleteDisabled
                ? "Suppression indisponible"
                : "Supprimer",
              disabled: isDeleteDisabled,
              danger: !isDeleteDisabled,
              onClick: () =>
                Modal.confirm({
                  title: "Confirmation",
                  content: "Voulez-vous vraiment supprimer cette vente ?",
                  onOk: async () => {
                    if (!canDelete) return;
                    try {
                      await api.deleteVente(record.id);
                      void invalidateSales();
                      dispatch({
                        type: "DELETE_VENTE",
                        payload: record.id,
                      });
                      message.success("Vente supprimée avec succès");
                    } catch (error) {
                      message.error("Erreur lors de la suppression");
                    }
                  },
                }),
            });
          }
        }

        return (
          <RowActionsMenu
            items={actionItems}
            ariaLabel={`Afficher les actions de la vente ${
              record.invoice_number || record.id
            }`}
          />
        );
      },
    },
  ];

  // Configuration pour les lignes expansibles
  const expandable = {
    columnWidth: 44,
    expandedRowRender: (record: any) => {
      const items = record.produitsVendus || [];
      const getItemUnitLabel = (item: any): string => {
        if (item?.item_type === "service") {
          return "Prestation";
        }
        const productId = item?.product_id ?? item?.produit_id;
        const productFromState = findProductById(productId);
        const unitId =
          item?.resolved_unit_id ??
          item?.unit_id ??
          item?.unit?.id ??
          productFromState?.unit_id ??
          productFromState?.unit?.id;
        const mappedUnit =
          unitId !== undefined && unitId !== null
            ? unitsById[Number(unitId)]
            : undefined;

        const unitLabel =
          item?.unit_short_name ||
          item?.unit_name ||
          item?.unit?.short_name ||
          item?.unit?.name ||
          productFromState?.unit_short_name ||
          productFromState?.unit_name ||
          productFromState?.unit?.short_name ||
          productFromState?.unit?.name ||
          mappedUnit?.short_name ||
          mappedUnit?.name;

        return unitLabel && String(unitLabel).trim()
          ? String(unitLabel).trim()
          : "Non définie";
      };
      const detailColumns = [
        {
          title: "Article / Service",
          dataIndex: "product_id",
          key: "produit",
          render: (product_id: any, item: any): string =>
            item?.product_name ||
            (item?.item_type === "service"
              ? `Service ${item?.service_id ?? "inconnu"}`
              : resolveProductName(product_id)),
        },
        {
          title: "Quantité",
          dataIndex: "quantity",
          key: "quantite",
          render: (value: any): string => formatQuantity(value),
        },
        {
          title: "Unité",
          dataIndex: "unit_short_name",
          key: "unite",
          render: (_: any, item: any): string => getItemUnitLabel(item),
        },
        {
          title: "Prix unitaire",
          dataIndex: "unit_price",
          key: "prix_unitaire",
          render: (value: any): string => `${formatNumber(value)} CFA`,
        },
        {
          title: "Remise",
          dataIndex: "discount_rate",
          key: "remise",
          render: (value: any): string => `${formatNumber(value || 0)} CFA`,
        },
        {
          title: "Taxe",
          dataIndex: "tax_id",
          key: "taxe",
          render: (tax_id: any): string => {
            if (!tax_id) return "Aucune";
            const tax = state.taxes.find((t: any) => t.id === tax_id);
            return tax ? `${tax.name} (${tax.rate}%)` : "Inconnue";
          },
        },
        {
          title: "Total",
          dataIndex: "subtotal",
          key: "total",
          render: (value: any): string => `${formatNumber(value)} CFA`,
        },
      ];
      return (
        <Table
          dataSource={items}
          columns={detailColumns}
          pagination={false}
          bordered
          size="small"
          rowKey={(record: any, index) =>
            record.id ? record.id.toString() : (index ?? 0).toString()
          }
        />
      );
    },
    onExpand: (expanded: boolean, record: any) => {
      if (expanded) {
        setExpandedRowKeys((prev) => [...prev, record.id]);
        loadVenteDetails(record);
      } else {
        setExpandedRowKeys((prev) => prev.filter((key) => key !== record.id));
      }
    },
    expandedRowKeys: expandedRowKeys,
  };

  // Ajouter company_id aux paramètres de recherche
  // SUPPRESSION du useEffect problématique qui causait une boucle infinie
  // Ce useEffect était en conflit avec refreshSales dans le composant parent
  // et appelait api.fetchVentes sans dispatcher le résultat

  // Générer la facture puis l'ouvrir dans le lecteur PDF du navigateur.
  const handleOpenInvoice = async (record: any) => {
    // Permission Check: Assumes 'view' is sufficient for preview/print
    if (!checkPermission(typedAuth, "Gestion Commerciale.Ventes.Ventes.view")) {
      message.error(
        "Vous n'avez pas la permission d'ouvrir les factures.",
      );
      return;
    }

    // Ouvrir immédiatement l'onglet afin que le navigateur ne le considère
    // pas comme une pop-up déclenchée après les appels asynchrones.
    const previewWindow = window.open("", "_blank");
    if (!previewWindow) {
      message.warning(
        "Impossible d'ouvrir l'aperçu. Autorisez les fenêtres pop-up pour ELSA GESTION.",
      );
      return;
    }

    previewWindow.document.title = "Préparation de la facture";
    previewWindow.document.body.style.margin = "0";
    previewWindow.document.body.style.minHeight = "100vh";
    previewWindow.document.body.style.display = "grid";
    previewWindow.document.body.style.placeItems = "center";
    previewWindow.document.body.style.fontFamily =
      "Inter, Arial, sans-serif";
    previewWindow.document.body.style.color = "#2c5282";
    previewWindow.document.body.textContent = "Préparation de la facture…";

    try {
      setLoading(true);
      message.loading({
        content: "Préparation de la facture...",
        key: "invoice-loading",
      });

      // 1. Récupérer les détails complets de la vente
      const scopedParams = buildScopedApiParams(record);
      const venteDetail = await api.fetchVenteDetail(record.id, scopedParams);

      // 2. Récupérer les items si nécessaire
      let produitsVendus = venteDetail.produitsVendus || [];
      if (!produitsVendus.length) {
        const orderItems = await api.fetchOrderItems(record.id, scopedParams);
        produitsVendus = orderItems;
      }

      // 3. Récupérer les détails complets des produits avec leurs unités
      try {
        const productIds = produitsVendus
          .map((p: any) => p.product_id)
          .filter(Boolean);

        if (productIds.length > 0) {
          const contextCompanyId =
            (record?.company_id as number) ||
            (selectedCompany?.id ?? selectedCompany);
          const contextWarehouseId =
            (record?.warehouse_id as number) ||
            (selectedWarehouse?.id ?? selectedWarehouse);

          const productDetails = await api.fetchProductDetails(productIds, {
            company_id: contextCompanyId,
            warehouse_id: contextWarehouseId,
          });

          // Enrichir les produits vendus avec leurs détails complets
          produitsVendus = produitsVendus.map((produit: any) => {
            const details = productDetails.find(
              (p: any) => p.id === produit.product_id,
            );
            if (details) {
              return {
                ...produit,
                unit_short_name: details.unit_short_name,
                unit_name: details.unit_name,
                product_name: details.name || produit.product_name,
              };
            }
            return produit;
          });
        }
      } catch (detailsError) {
        console.error(
          "Erreur lors de l'enrichissement des produits:",
          detailsError,
        );
        // Continuer avec les données existantes
      }

      // 4. Récupérer les informations du magasin
      const warehouse = await api.fetchWarehouse(record.warehouse_id);

      // 5. Récupérer les paiements associés à cette commande
      let payments = [];
      try {
        const paymentQueryParams = new URLSearchParams();
        const scopedCompanyId =
          record?.company_id ?? selectedCompany?.id ?? selectedCompany;
        const scopedWarehouseId =
          record?.warehouse_id ?? selectedWarehouse?.id ?? selectedWarehouse;

        if (scopedCompanyId) {
          paymentQueryParams.set("company_id", String(scopedCompanyId));
        }
        if (scopedWarehouseId) {
          paymentQueryParams.set("warehouse_id", String(scopedWarehouseId));
        }

        const paymentsEndpoint = `${BASE_URL}/api/orders/${record.id}/payments${
          paymentQueryParams.toString()
            ? `?${paymentQueryParams.toString()}`
            : ""
        }`;

        const response = await fetch(paymentsEndpoint, {
          headers: getAuthHeaders(),
        });
        if (response.ok) {
          payments = await response.json();
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paiements:", error);
        // Continuer sans les paiements
      }

      const createdByName =
        venteDetail?.created_by_name ||
        venteDetail?.staff_name ||
        venteDetail?.staff_member?.name ||
        "SysAdmin";
      const printedByName =
        typedAuth?.user?.name || typedAuth?.user?.email || "N/A";

      // 6. Préparer les données pour la facture
      const invoiceData = {
        ...venteDetail,
        produitsVendus,
        warehouse,
        payments, // Ajouter les paiements
        client: state.clients.find((c) => c.id === venteDetail.user_id),
        created_by_name: createdByName,
        printed_by_name: printedByName,
      };

      // 7. Générer le HTML de la facture en utilisant le template existant
      const invoiceHTML = generateInvoiceHTML({
        order: invoiceData,
        clientObj: invoiceData.client,
        warehouse: warehouse,
        paymentStatus: invoiceData.payment_status,
        montantVerse: invoiceData.paid_amount || 0,
        montantDu: invoiceData.due_amount || 0,
        totals: {
          subtotal: invoiceData.subtotal,
          totalDiscount: invoiceData.discount || 0,
          totalTax: invoiceData.tax_amount || 0,
          total: invoiceData.total,
        },
        formattedDate: dayjs(invoiceData.order_date).format("DD/MM/YYYY"),
        formatNumber,
      });

      // 8. Nom du fichier
      const fileName = `Facture_${invoiceData.invoice_number}.pdf`;

      // 9. Appeler l'API backend pour générer le PDF
      // 🎯 NOUVELLE APPROCHE: Utiliser html-pdf-node (beaucoup plus léger)
      const useReactPdf = true; // ✅ Utilise React-PDF (Digital Ocean compatible, pure JavaScript)

      try {
        let response;

        if (useReactPdf) {
          // 🎯 NOUVELLE API: React-PDF (Digital Ocean compatible)
          response = await fetch(
            `${BASE_URL}/api/orders/generate-sale-invoice-pdf-react`,
            {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                orderData: {
                  ...invoiceData,
                  // Passer les données nécessaires pour React-PDF
                  clients: [
                    invoiceData.client || {
                      id: invoiceData.user_id,
                      name: "Client inconnu",
                    },
                  ],
                  produits:
                    invoiceData.produitsVendus?.map((item: any) => ({
                      id: item.id_produit,
                      nom_produit: item.product_name,
                      unit_short_name: item.unit_short_name || "unité",
                    })) || [],
                  taxes: [], // À enrichir si nécessaire
                },
                fileName: fileName,
              }),
            },
          );
        } else {
          // 🐌 ANCIENNE API: Puppeteer (compatible mais lourde)
          response = await fetch(
            `${BASE_URL}/api/orders/generate-sale-invoice-pdf-v2`,
            {
              method: "POST",
              headers: getAuthHeaders(),
              body: JSON.stringify({
                htmlContent: invoiceHTML,
                fileName: fileName,
              }),
            },
          );
        }

        if (!response.ok) {
          // Si la réponse n'est pas OK, tenter d'extraire le message d'erreur
          let errorMessage = "Erreur serveur lors de la génération du PDF";
          try {
            const errorText = await response.text();
            try {
              const errorData = JSON.parse(errorText);
              errorMessage =
                errorData.error || errorData.details || errorMessage;
            } catch (parseError) {
              errorMessage = errorText || errorMessage;
            }
          } catch (e) {
            console.error(
              "📄 [PDF DEBUG] Impossible de lire la réponse d'erreur:",
              e,
            );
          }
          throw new Error(errorMessage);
        }

        // Récupérer le PDF et lui conserver un nom explicite dans le lecteur.
        const pdfBlob = await response.blob();
        const pdfFile = new File([pdfBlob], fileName, {
          type: pdfBlob.type || "application/pdf",
        });
        const pdfUrl = URL.createObjectURL(pdfFile);

        if (previewWindow.closed) {
          URL.revokeObjectURL(pdfUrl);
          throw new Error("L'onglet d'aperçu a été fermé.");
        }

        previewWindow.opener = null;
        previewWindow.location.replace(pdfUrl);

        // Laisser le temps au lecteur PDF de charger le document et de rendre
        // disponibles ses boutons d'impression et de téléchargement.
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 5 * 60 * 1000);

        const invoiceFormatLabel =
          warehouse?.sales_invoice_print_layout === "a5_single"
            ? "A5 natif"
            : warehouse?.sales_invoice_print_layout === "a4_two_a5"
              ? "A4 paysage · 2 exemplaires A5"
              : "A4 standard";

        message.success({
          content: `Facture ouverte — format ${invoiceFormatLabel}`,
          key: "invoice-loading",
        });
      } catch (pdfError: unknown) {
        if (!previewWindow.closed) {
          previewWindow.close();
        }
        console.error("Erreur lors de la génération du PDF:", pdfError);
        message.error({
          content:
            "Erreur lors de la génération de la facture: " +
            (pdfError instanceof Error ? pdfError.message : "Erreur inconnue"),
          key: "invoice-loading",
        });
      }
    } catch (error) {
      if (!previewWindow.closed) {
        previewWindow.close();
      }
      console.error("Erreur lors de l'ouverture de la facture:", error);
      message.error({
        content: `Erreur lors de l'ouverture de la facture: ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`,
        key: "invoice-loading",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReprintReceipt = async (record: any) => {
    // Permission Check: Assumes 'view' is sufficient for receipt preview/print
    if (!checkPermission(typedAuth, "Gestion Commerciale.Ventes.Ventes.view")) {
      message.error(
        "Vous n'avez pas la permission de réimprimer les tickets de caisse.",
      );
      return;
    }

    if (record?.__offline) {
      message.error(
        "Impossible d'afficher le ticket pour une vente hors-ligne non synchronisée.",
      );
      return;
    }

    try {
      setLoading(true);
      message.loading({
        content: "Chargement du ticket de caisse...",
        key: "receipt-loading",
      });

      const response = await fetch(
        `${BASE_URL}/api/orders/${record.id}/receipt`,
        {
          headers: getAuthHeaders(),
        },
      );

      if (!response.ok) {
        let errorMessage = "Erreur lors de la récupération du ticket de caisse";
        try {
          const errorText = await response.text();
          try {
            const errorData = JSON.parse(errorText);
            errorMessage =
              errorData.message ||
              errorData.error ||
              errorData.details ||
              errorMessage;
          } catch (parseError) {
            errorMessage = errorText || errorMessage;
          }
        } catch (e) {
          // ignore
        }
        throw new Error(errorMessage);
      }

      const receiptData = await response.json();
      setTicketData(receiptData);
      setTicketVisible(true);

      message.success({
        content: "Ticket prêt pour impression",
        key: "receipt-loading",
      });
    } catch (error) {
      console.error("Erreur lors du chargement du ticket de caisse:", error);
      message.error({
        content: `Erreur lors du chargement du ticket: ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`,
        key: "receipt-loading",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDeliveryNote = async (
    deliveryId: number,
    deliveryNumber?: string | null,
  ) => {
    if (
      !checkPermission(
        typedAuth,
        "Gestion Commerciale.Ventes.Livraisons.generate_pdf",
      )
    ) {
      message.error(
        "Vous n'avez pas la permission de télécharger les bordereaux de livraison.",
      );
      return;
    }

    if (!deliveryId) {
      message.error("Bordereau de livraison introuvable.");
      return;
    }

    try {
      setLoading(true);
      message.loading({
        content: "Préparation du bordereau de livraison...",
        key: "delivery-note-loading",
      });
      await downloadSalesDeliveryPdf(
        deliveryId,
        `Bordereau_${deliveryNumber || deliveryId}.pdf`,
      );
      message.success({
        content: "Bordereau téléchargé avec succès",
        key: "delivery-note-loading",
      });
    } catch (error) {
      message.error({
        content: `Erreur lors du téléchargement du bordereau: ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`,
        key: "delivery-note-loading",
      });
    } finally {
      setLoading(false);
    }
  };

  // Nouvelle fonction pour exporter les ventes vers Excel
  /**
   * Construit les paramètres de requête + le libellé des filtres appliqués,
   * à partir des filtres actuellement actifs sur le tableau des ventes.
   * Partagé par les exports Excel et PDF pour garantir un périmètre identique.
   */
  const buildSalesExportRequest = () => {
    const warehouseId =
      typeof selectedWarehouse === "object"
        ? (selectedWarehouse as any)?.id
        : selectedWarehouse;
    const companyId =
      (typeof selectedWarehouse === "object" &&
        (selectedWarehouse as any)?.company_id) ||
      (typeof selectedCompany === "object"
        ? (selectedCompany as any)?.id
        : selectedCompany);
    const companyName =
      (typeof selectedCompany === "object" &&
        (selectedCompany as any)?.name) ||
      null;

    const params: any = {
      page: 1,
      limit: 1000, // Limite élevée pour récupérer toutes les données
      include_summary: "true",
      sales_channel: state.salesChannel,
    };

    const filtresAppliques: string[] = [];
    let warehouseName: string | null = null;
    let periodLabel: string | null = null;
    let clientName: string | null = null;

    // Filtre de magasin (via RightSideNav)
    // Note : l'entrée « Établissement: <nom réel> » du bandeau de filtres est
    // injectée côté backend (nom récupéré en base), on ne la pousse pas ici
    // pour l'export PDF afin d'éviter le doublon « ID ». Elle reste dans le
    // libellé de succès Excel via warehouseName.
    if (warehouseId) {
      params.warehouse = warehouseId;
      warehouseName =
        (typeof selectedWarehouse === "object" && selectedWarehouse?.name) ||
        `ID ${warehouseId}`;
    }
    if (companyId) {
      params.company_id = companyId;
    }

    // Filtre de client
    if (state.selectedClient) {
      params.user_id = state.selectedClient.id;
      clientName =
        state.selectedClient.name ||
        state.selectedClient.company_name ||
        state.selectedClient.Nom_Raison_Sociale ||
        `Client ID ${state.selectedClient.id}`;
      filtresAppliques.push(`Client: ${clientName}`);
    }

    // Filtre de recherche textuelle
    if (state.searchTerm) {
      params.search = state.searchTerm.trim();
      filtresAppliques.push(`Recherche: "${state.searchTerm}"`);
    }

    if (state.salesChannel !== "all") {
      const channelLabel =
        SALES_CHANNEL_OPTIONS.find(
          (channel) => channel.value === state.salesChannel,
        )?.label || state.salesChannel;
      filtresAppliques.push(`Canal: ${channelLabel}`);
    }

    // Filtre de plage de dates
    if (state.dateRange.start && state.dateRange.end) {
      params.dateDebut = state.dateRange.start;
      params.dateFin = state.dateRange.end;
      periodLabel = `du ${state.dateRange.start} au ${state.dateRange.end}`;
      filtresAppliques.push(`Période: ${periodLabel}`);
    }

    // Filtre de statut de paiement (par onglet actif)
    if (state.activeTab !== "all") {
      let statusFilter = "";
      switch (state.activeTab) {
        case "unpaid":
          statusFilter = "Non payé";
          break;
        case "partially_paid":
          statusFilter = "Partiellement payé";
          break;
        case "paid":
          statusFilter = "Payé";
          break;
      }

      if (statusFilter) {
        params.payment_status = statusFilter;
        filtresAppliques.push(`Statut de paiement: ${statusFilter}`);
      }
    }

    // Filtre pour inclure ou non les ventes supprimées
    params.include_deleted = state.showDeleted ? "true" : "false";
    if (state.showDeleted) {
      filtresAppliques.push("Inclut les ventes supprimées");
    }

    return {
      params,
      filtresAppliques,
      warehouseName,
      warehouseId,
      companyId,
      companyName,
      periodLabel,
      clientName,
    };
  };

  const handleExportToExcel = async () => {
    if (!(await ensureSubscriptionActionAllowed('data_export'))) return;
    // Permission Check: Assume 'view' is sufficient for export
    if (!checkPermission(typedAuth, "Gestion Commerciale.Ventes.Ventes.view")) {
      message.error("Vous n'avez pas la permission d'exporter les ventes.");
      return;
    }
    try {
      setLoading(true);
      message.loading({
        content: "Préparation de l'export Excel...",
        key: "exportLoading",
      });

      // Utiliser les mêmes filtres que ceux appliqués actuellement au tableau
      const { params, filtresAppliques } = buildSalesExportRequest();

      // Récupérer les données filtrées
      const response = await fetch(
        `${BASE_URL}/api/orders/sales-ledger?${new URLSearchParams(params).toString()}`,
        {
          headers: getAuthHeaders(),
        },
      );
      if (!response.ok) {
        throw new Error(
          await getResponseErrorMessage(response, "Impossible d'exporter les ventes"),
        );
      }

      const data = await response.json();
      const ventesToExport = data.orders || [];

      // Si aucune vente trouvée, afficher un message et arrêter
      if (ventesToExport.length === 0) {
        message.warning({
          content: "Aucune vente trouvée avec les filtres appliqués",
          key: "exportLoading",
        });
        setLoading(false);
        return;
      }

      // Formater les données pour l'export
      const formattedData = ventesToExport.map((vente: any) => {
        // Trouver le client
        const client = state.clients.find((c) => c.id === vente.user_id);
        const clientName =
          vente.entity_name ||
          vente.user_name ||
          (client
            ? client.name ||
              client.company_name ||
              client.Nom_Raison_Sociale
            : null) ||
          "Client inconnu";
        const channel = getSalesChannelDisplay(vente.sales_channel);

        return {
          Date: dayjs(vente.order_date).format("DD/MM/YYYY"),
          "N° Facture": vente.invoice_number,
          Client: clientName,
          Établissement: vente.warehouse_name || "Non spécifié",
          Canal: channel.label,
          Statut: vente.order_status || "Non spécifié",
          "Statut paiement": vente.payment_status || "Non spécifié",
          "Montant HT": vente.subtotal || 0,
          Taxes: vente.tax_amount || 0,
          Remise: vente.discount || 0,
          "Montant total": vente.total || 0,
          "Montant payé": vente.paid_amount || 0,
          "Reste à payer": vente.due_amount || 0,
          Devise: "XOF",
        };
      });

      // Créer un classeur Excel
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Ventes");

      // Ajuster la largeur des colonnes
      const columnWidths = [
        { wch: 12 }, // Date
        { wch: 15 }, // N° Facture
        { wch: 25 }, // Client
        { wch: 20 }, // Magasin
        { wch: 20 }, // Canal
        { wch: 15 }, // Statut
        { wch: 18 }, // Statut paiement
        { wch: 15 }, // Montant HT
        { wch: 15 }, // Taxes
        { wch: 15 }, // Remise
        { wch: 15 }, // Montant total
        { wch: 15 }, // Montant payé
        { wch: 15 }, // Reste à payer
        { wch: 8 }, // Devise
      ];
      worksheet["!cols"] = columnWidths;

      // Générer le fichier Excel
      const today = dayjs().format("YYYY-MM-DD");
      const filename = `Ventes_${today}.xlsx`;
      XLSX.writeFile(workbook, filename);

      // Afficher un message de succès avec les filtres appliqués
      const filtresMsg =
        filtresAppliques.length > 0
          ? `Filtres appliqués: ${filtresAppliques.join(", ")}`
          : "Aucun filtre appliqué";

      message.success({
        content: (
          <>
            <div>Export Excel réussi!</div>
            <div
              style={{ fontSize: "12px", marginTop: "5px" }}
            >{`${ventesToExport.length} ventes exportées dans "${filename}"`}</div>
            <div style={{ fontSize: "12px" }}>{filtresMsg}</div>
          </>
        ),
        key: "exportLoading",
        duration: 5, // Afficher le message plus longtemps
      });
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      message.error({
        content: `Erreur lors de l'export Excel: ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`,
        key: "exportLoading",
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Export PDF professionnel de la liste des ventes (mode "detail" ou "summary").
   * Réutilise le même périmètre de filtres que l'export Excel. Le backend relit
   * les ventes dans le warehouse actif, génère le PDF et retourne le fichier.
   */
  const handleExportToPdf = async (mode: "detail" | "summary") => {
    if (!checkPermission(typedAuth, "Gestion Commerciale.Ventes.Ventes.view")) {
      message.error("Vous n'avez pas la permission d'exporter les ventes.");
      return;
    }
    try {
      setLoading(true);
      message.loading({
        content: "Préparation de l'export PDF...",
        key: "exportPdfLoading",
      });

      const {
        params,
        filtresAppliques,
        warehouseName,
        warehouseId,
        companyId,
        companyName,
        periodLabel,
        clientName,
      } = buildSalesExportRequest();

      if (!warehouseId) {
        message.warning({
          content: "Sélectionnez un établissement avant l'export PDF.",
          key: "exportPdfLoading",
        });
        return;
      }

      const pdfResponse = await fetch(
        `${BASE_URL}/api/orders/generate-sales-list-pdf`,
        {
          method: "POST",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            mode,
            company_id: companyId || null,
            warehouse_id: warehouseId,
            filters: params,
            meta: {
              warehouseId,
              warehouseName: warehouseName || null,
              companyId: companyId || null,
              companyName: companyName || null,
              periodLabel: periodLabel || null,
              clientName: clientName || null,
              filtersApplied: filtresAppliques,
              currency: "XOF",
            },
          }),
        },
      );

      if (!pdfResponse.ok) {
        throw new Error(
          await getResponseErrorMessage(
            pdfResponse,
            "Impossible de générer le rapport PDF",
          ),
        );
      }

      const blob = await pdfResponse.blob();
      const reportRowCount = Number(
        pdfResponse.headers.get("X-Report-Row-Count"),
      );
      const today = dayjs().format("YYYY-MM-DD");
      const suffix =
        mode === "summary" ? "resume-par-client" : "details-factures";
      const filename = `Grand_livre_clients_${suffix}_${today}.pdf`;

      const url = window.URL.createObjectURL(blob);
      const downloadLink = document.createElement("a");
      downloadLink.href = url;
      downloadLink.download = filename;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(url);

      const filtresMsg =
        filtresAppliques.length > 0
          ? `Filtres appliqués: ${filtresAppliques.join(", ")}`
          : "Aucun filtre appliqué";
      message.success({
        content: (
          <>
            <div>Export PDF réussi!</div>
            <div style={{ fontSize: "12px", marginTop: "5px" }}>
              {Number.isFinite(reportRowCount) && reportRowCount > 0
                ? `${reportRowCount} ventes exportées dans "${filename}"`
                : `Rapport exporté dans "${filename}"`}
            </div>
            <div style={{ fontSize: "12px" }}>{filtresMsg}</div>
          </>
        ),
        key: "exportPdfLoading",
        duration: 5,
      });
    } catch (error) {
      console.error("Erreur lors de l'export PDF:", error);
      message.error({
        content: `Erreur lors de l'export PDF: ${
          error instanceof Error ? error.message : "Erreur inconnue"
        }`,
        key: "exportPdfLoading",
      });
    } finally {
      setLoading(false);
    }
  };

  // Define handleMultipleDelete function
  const handleMultipleDelete = () => {
    // Permission Check
    if (
      !checkPermission(typedAuth, "Gestion Commerciale.Ventes.Ventes.delete")
    ) {
      message.error("Vous n'avez pas la permission de supprimer des ventes.");
      return;
    }
    if (state.selectedVenteIds.length === 0) return;

    Modal.confirm({
      title: `Êtes-vous sûr de vouloir supprimer ${state.selectedVenteIds.length} vente(s) ?`,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: async () => {
        try {
          setLoading(true);
          // Use Promise.all to delete multiple ventes in parallel
          const deletePromises = state.selectedVenteIds.map((id) =>
            api.deleteVente(id).then((result) => { void invalidateSales(); return result; }),
          );
          await Promise.all(deletePromises);
          message.success(
            `${state.selectedVenteIds.length} vente(s) supprimée(s) avec succès`,
          );
          // Clear selected IDs
          dispatch({ type: "CLEAR_SELECTED_VENTE_IDS" });
          // Refresh sales list
          await refreshSales();
        } catch (error) {
          console.error("Erreur lors de la suppression des ventes:", error);
          message.error("Erreur lors de la suppression des ventes");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  return (
    <>
      <div className="sales-list__toolbar">
        {/* Multiple Delete Button */}
        {checkPermission(
          typedAuth,
          "Gestion Commerciale.Ventes.Ventes.delete",
        ) &&
          state.selectedVenteIds.length > 0 && (
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
              onClick={handleMultipleDelete}
            >
              Supprimer ({state.selectedVenteIds.length})
            </Button>
          )}

        {/* Export Menu (Excel + PDF) */}
        {checkPermission(
          typedAuth,
          "Gestion Commerciale.Ventes.Ventes.view",
        ) && (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: "excel",
                  icon: (
                    <FileExcelOutlined style={{ color: "#217346" }} />
                  ),
                  label: "Exporter sur Excel",
                  onClick: handleExportToExcel,
                },
                { type: "divider" },
                {
                  key: "pdf-detail",
                  icon: <FilePdfOutlined style={{ color: "#d32029" }} />,
                  label: "Exporter en PDF (liste détaillée)",
                  onClick: () => handleExportToPdf("detail"),
                },
                {
                  key: "pdf-summary",
                  icon: <FilePdfOutlined style={{ color: "#d32029" }} />,
                  label: "Exporter en PDF (résumé par client)",
                  onClick: () => handleExportToPdf("summary"),
                },
              ],
            }}
          >
            <Button icon={<DownloadOutlined />}>
              Exporter <DownOutlined />
            </Button>
          </Dropdown>
        )}
      </div>

      <div className="sales-list__table-wrap">
        <Table
          className="sales-list__table"
          dataSource={state.ventes}
          columns={columns}
          rowKey="id"
          size="middle"
          loading={state.loading || loading || linkedSaleLoading}
          scroll={{ x: 1460 }}
          expandable={expandable}
          pagination={{
            current: state.currentPage,
            pageSize: state.itemsPerPage,
            total: state.totalRecords,
            onChange: (page, pageSize) => {
              if (pageSize !== state.itemsPerPage) {
                dispatch({ type: "SET_ITEMS_PER_PAGE", payload: pageSize });
                dispatch({ type: "SET_PAGE", payload: 1 });
              } else {
                dispatch({ type: "SET_PAGE", payload: page });
              }
            },
            showSizeChanger: true,
            showQuickJumper: false,
            showLessItems: true,
            showTotal: (total, range) =>
              `Affichage de ${range[0]} à ${range[1]} sur ${total} ventes`,
            pageSizeOptions: ["10", "20", "50", "100"],
            position: ["bottomRight"],
          }}
          sortDirections={["descend", "ascend"]}
        />
      </div>

      {/* Modalités pour les détails de la vente et autres fonctionnalités à ajouter */}
      <VenteDetailModal
        order={detailVente}
        visible={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          clearSaleLink();
        }}
        produits={state.produits}
        taxes={state.taxes}
        clients={state.clients}
        refreshOrderDetails={loadVenteDetailsForModal}
        refreshSales={refreshSales}
        onPrintDeliveryNote={
          checkPermission(
            typedAuth,
            "Gestion Commerciale.Ventes.Livraisons.generate_pdf",
          )
            ? handleDownloadDeliveryNote
            : undefined
        }
      />

      {state.showForm && (
        <VenteForm
          visible={state.showForm}
          onClose={() => dispatch({ type: "TOGGLE_FORM", payload: false })}
          refreshSales={refreshSales}
          warehouseName={
            warehouseData?.name ||
            warehouseData?.warehouse_name ||
            warehouseData?.data?.name ||
            warehouseData?.data?.warehouse_name ||
            null
          }
        />
      )}

      {/* Composant pour générer et télécharger les factures */}
      {invoiceData && (
        <InvoicePDF
          order={invoiceData}
          clients={state.clients}
          produits={state.produits}
          taxes={state.taxes}
          companyName={warehouseData?.name || "ELSA Technologies"}
          warehouseName={warehouseData?.name || "ELSA Technologies"}
          warehouse={invoiceData.warehouse || warehouseData}
        />
      )}

      {/* Composant pour réafficher / réimprimer le ticket de caisse */}
      <TicketCaisse
        visible={ticketVisible}
        order={ticketData}
        onClose={() => {
          setTicketVisible(false);
          setTicketData(null);
        }}
      />
    </>
  );
};

// Mobile reuses the form and reducer without mounting the desktop list workspace.
export function SaleFormWorkspace({ order = null, onClose, onSaved }: {
  order?: any; onClose: () => void; onSaved: () => Promise<void>;
}) {
  const [state, dispatch] = useReducer(ventesReducer, { ...initialState, showForm: true,
    formData: order ? saleFormData(order, order.items || order.produitsVendus || [])
      : { ...initialState.formData, Date_Facture: dayjs().format("YYYY-MM-DD"), produitsVendus: [] } });
  const auth = useAuth();
  return <VentesContext.Provider value={{ state, dispatch, api, auth,
    restoredDraftMeta: null, clearRestoredSalesDraft: () => {} }}>
    <VenteForm visible onClose={onClose} refreshSales={onSaved} />
  </VentesContext.Provider>;
}

const GestionVentes: React.FC = () => {
  const [state, dispatch] = useReducer(ventesReducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedWarehouse, selectedCompany } = useSelection();
  const selectedCompanyId = Number(
    (selectedCompany as any)?.id ?? selectedCompany,
  );
  const { taxes } = useTaxes(api, {
    companyId:
      Number.isInteger(selectedCompanyId) && selectedCompanyId > 0
        ? selectedCompanyId
        : null,
  });
  const BASE_URL = API_CONFIG.BASE_URL;
  // Get auth at the top level to pass down via context
  const auth = useAuth();
  const canCreateSaleDraft = checkPermission(
    auth,
    "Gestion Commerciale.Ventes.Ventes.create",
  );
  const canViewSaleDraft = checkPermission(
    auth,
    "Gestion Commerciale.Ventes.Ventes.view",
  );
  const [salesCatalogLoaded, setSalesCatalogLoaded] =
    useState<boolean>(false);
  const [restoredDraftMeta, setRestoredDraftMeta] = useState<{
    draftId: string;
    missingItemCount: number;
    priceChangedCount: number;
  } | null>(null);
  const restoredDraftHandledRef = useRef<string | null>(null);
  const salesRequestRef = useRef(0);

  // État pour les clients filtrés par magasin (pour le filtre principal)
  const [filteredClientsForFilter, setFilteredClientsForFilter] = useState<
    any[]
  >([]);

  // IMPORTANT: Move all hooks before any conditionals
  useEffect(() => {
    dispatch({ type: "SET_TAXES", payload: taxes });
  }, [taxes]);

  // La liste reçue est déjà filtrée côté backend (entreprise + visibilité magasin)
  useEffect(() => {
    setFilteredClientsForFilter(state.clients);
  }, [state.clients]);

  // Combined useEffect for fetching data based on warehouse/company changes
  useEffect(() => {
    const warehouseId = selectedWarehouse?.id ?? selectedWarehouse; // Handle object or ID
    const companyId = selectedCompany?.id ?? selectedCompany;
    let active = true;
    setSalesCatalogLoaded(false);

    if (warehouseId) {
      // Fetch initial clients and products for the selected warehouse
      const loadInitialData = async () => {
        dispatch({ type: "SET_LOADING", payload: true });
        dispatch({ type: "SET_ERROR", payload: null });
        try {
          const clientsResponse = await api.fetchClients(companyId, warehouseId);
          if (!active) return;
          dispatch({ type: "SET_CLIENTS", payload: clientsResponse });
          dispatch({ type: "SET_PRODUITS", payload: [] });
          setSalesCatalogLoaded(true);
        } catch (error) {
          if (!active) return;
          console.error(
            "Erreur lors du chargement initial des données:",
            error,
          );
          dispatch({
            type: "SET_ERROR",
            payload: "Erreur lors du chargement des données initiales.",
          });
          message.error("Erreur lors du chargement des données.");
        }
      };
      loadInitialData();
    } else {
      // Reset lists if no warehouse is selected
      dispatch({ type: "SET_CLIENTS", payload: [] });
      dispatch({ type: "SET_PRODUITS", payload: [] });
      dispatch({ type: "SET_VENTES", payload: { ventes: [], total: 0 } });
      dispatch({ type: "SET_LOADING", payload: false });
      setSalesCatalogLoaded(true);
    }

    return () => {
      active = false;
    };
  }, [selectedWarehouse, selectedCompany]); // Depend on selectedWarehouse and selectedCompany

  const clearRestoredSalesDraft = useCallback(() => {
    const returnContext = location.state?.stockReplenishmentReturn;
    if (returnContext?.source_type !== "sale") {
      setRestoredDraftMeta(null);
      return;
    }

    const finalizedDraftId = finalizeSalesReplenishmentDraft(
      returnContext.sales_draft_id,
    );
    if (finalizedDraftId) {
      // Le contexte de navigation peut rester visible pendant un rendu après
      // la suppression. Garder l'identifiant traité empêche sa restauration.
      restoredDraftHandledRef.current = finalizedDraftId;
    }
    setRestoredDraftMeta(null);

    const nextState = { ...(location.state || {}) };
    delete nextState.stockReplenishmentReturn;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: Object.keys(nextState).length > 0 ? nextState : null,
      },
    );
  }, [
    location.hash,
    location.pathname,
    location.search,
    location.state,
    navigate,
  ]);

  useEffect(() => {
    const returnContext = location.state?.stockReplenishmentReturn;
    const draftId = returnContext?.sales_draft_id;
    if (
      returnContext?.source_type !== "sale" ||
      !draftId ||
      !salesCatalogLoaded ||
      !auth?.user?.id ||
      !selectedCompany ||
      !selectedWarehouse ||
      restoredDraftHandledRef.current === draftId
    ) {
      return;
    }

    restoredDraftHandledRef.current = draftId;
    let active = true;
    let settled = false;

    const restoreDraft = async () => {
      const draft = loadSalesReplenishmentDraft(draftId);
      const warehouseId = Number(
        (selectedWarehouse as any)?.id ?? selectedWarehouse,
      );
      const companyId = Number(
        (selectedCompany as any)?.id ?? selectedCompany,
      );
      const scopeMatches =
        draft &&
        draft.source_type === "sale" &&
        Number(draft.user_id) === Number(auth.user?.id) &&
        Number(draft.company_id) === companyId &&
        Number(draft.warehouse_id) === warehouseId;

      if (!canCreateSaleDraft || !canViewSaleDraft || !scopeMatches) {
        settled = true;
        message.error(
          !canCreateSaleDraft || !canViewSaleDraft
            ? "Vous n'avez plus les permissions nécessaires pour reprendre cette vente."
            : "Le brouillon de vente est expiré ou ne correspond plus à la session, à l'entreprise ou à l'établissement sélectionné.",
        );
        clearRestoredSalesDraft();
        return;
      }

      const draftFormData = draft.payload?.form_data || {};
      const draftItems = Array.isArray(draftFormData.produitsVendus)
        ? draftFormData.produitsVendus
        : [];
      const draftProductIds: number[] = Array.from(
        new Set<number>(
          draftItems
            .filter((item: any) => item?.item_type !== "service")
            .map((item: any) => Number(item?.product_id ?? item?.produit_id))
            .filter((id: number) => Number.isInteger(id) && id > 0),
        ),
      );
      const productDetails = await api.fetchProductDetails(draftProductIds, {
        company_id: companyId,
        warehouse_id: warehouseId,
      });
      if (!active) return;

      const exactProducts = (Array.isArray(productDetails)
        ? productDetails
        : []
      )
        .map((detail: any) => {
          const productId = Number(
            detail?.id ?? detail?.id_produit ?? detail?.product_id,
          );
          const fallback =
            state.produits.find(
              (product) => Number(product.id_produit) === productId,
            ) ||
            draftItems.find(
              (item: any) =>
                Number(item?.product_id ?? item?.produit_id) === productId,
            );
          return normalizeProductDetailForSale({
            ...(fallback || {}),
            ...detail,
            stock_management_mode:
              detail?.stock_management_mode ??
              fallback?.stock_management_mode,
          });
        })
        .filter(
          (product: Product) =>
            Number.isInteger(product.id_produit) && product.id_produit > 0,
        );
      const serviceIds = draftItems.filter((item: any) => item.item_type === "service").map(choiceItemId);
      const currentServices: Product[] = [];
      for (let offset = 0; offset < serviceIds.length; offset += 100) {
        const result = await fetchOrderFormOptions({ userId: auth.user?.id, companyId, warehouseId }, "sales", "create",
          { kind: "products", item_ids: serviceIds.slice(offset, offset + 100).join(",") });
        currentServices.push(...(result.products || []));
      }
      if (!active) return;
      const currentCatalog = [...exactProducts, ...currentServices];
      const reconciliation = reconcileManualSaleDraftItems(
        draftItems,
        currentCatalog,
      );
      if (reconciliation.items.length === 0) {
        settled = true;
        message.error(
          "Aucun article du panier sauvegardé n'est encore disponible dans cet établissement.",
        );
        clearRestoredSalesDraft();
        return;
      }

      const rawClientId =
        draftFormData.Client_ID && typeof draftFormData.Client_ID === "object"
          ? draftFormData.Client_ID.value
          : draftFormData.Client_ID;
      const clientId = Number(rawClientId) || null;
      const clientStillAvailable = clientId
        ? state.clients.some((client: any) => Number(client.id) === clientId)
        : false;
      const draftDate = dayjs(draftFormData.Date_Facture);

      dispatch({
        type: "SET_FORM_DATA",
        payload: {
          ...initialState.formData,
          Date_Facture: draftDate.isValid()
            ? draftDate.format("YYYY-MM-DD")
            : dayjs().format("YYYY-MM-DD"),
          Statut_Vente:
            typeof draftFormData.Statut_Vente === "string"
              ? draftFormData.Statut_Vente
              : initialState.formData.Statut_Vente,
          Client_ID:
            clientId && clientStillAvailable ? String(clientId) : "",
          produitsVendus: reconciliation.items,
          remise_globale: Math.max(
            0,
            Number(draftFormData.remise_globale) || 0,
          ),
          manual_discount: Math.max(
            0,
            Number(draftFormData.manual_discount) || 0,
          ),
          termes_conditions:
            typeof draftFormData.termes_conditions === "string"
              ? draftFormData.termes_conditions
              : "",
          remarques:
            typeof draftFormData.remarques === "string"
              ? draftFormData.remarques
              : "",
          invoice_subject:
            typeof draftFormData.invoice_subject === "string"
              ? draftFormData.invoice_subject
              : "",
          invoice_type:
            typeof draftFormData.invoice_type === "string"
              ? draftFormData.invoice_type
              : undefined,
        },
      });
      dispatch({ type: "TOGGLE_FORM", payload: true });
      setRestoredDraftMeta({
        draftId,
        missingItemCount: reconciliation.missingItemCount,
        priceChangedCount: reconciliation.priceChangedCount,
      });
      settled = true;

      if (clientId && !clientStillAvailable) {
        message.warning(
          "Le client du brouillon n'est plus disponible. Sélectionnez un client avant de valider.",
        );
      } else {
        message.success(
          "Panier de vente restauré et revalidé avec le stock actuel.",
        );
      }
    };

    void restoreDraft().catch((error) => {
      if (!active) return;
      settled = true;
      restoredDraftHandledRef.current = null;
      console.error("Erreur lors de la restauration du panier de vente:", error);
      message.error(
        "Impossible de recharger le panier sauvegardé. Réessayez en actualisant la page.",
      );
    });
    return () => {
      active = false;
      if (!settled && restoredDraftHandledRef.current === draftId) {
        restoredDraftHandledRef.current = null;
      }
    };
  }, [
    auth?.user,
    canCreateSaleDraft,
    canViewSaleDraft,
    clearRestoredSalesDraft,
    location.state,
    salesCatalogLoaded,
    selectedCompany,
    selectedWarehouse,
    state.clients,
    state.produits,
  ]);

  const refreshSales = useCallback(async () => {
    const requestId = ++salesRequestRef.current;
    const warehouseId = selectedWarehouse?.id ?? selectedWarehouse;
    const companyId = selectedCompany?.id ?? selectedCompany;

    if (!warehouseId) {
      dispatch({ type: "SET_VENTES", payload: { ventes: [], total: 0 } });
      dispatch({ type: "SET_LOADING", payload: false });
      return; // Don't fetch sales if no warehouse
    }

    dispatch({ type: "SET_LOADING", payload: true });
    dispatch({ type: "SET_ERROR", payload: null });
    try {
      const params: any = {
        page: state.currentPage,
        limit: state.itemsPerPage,
        search: state.searchTerm,
        warehouse: warehouseId,
        company_id: companyId, // Include companyId
        include_deleted: state.showDeleted ? "true" : "false",
        sales_channel: state.salesChannel,
        // Add default sorting for the list view
        sort_by: "order_date",
        order: "desc",
      };

      if (state.selectedClient) {
        params.user_id = state.selectedClient.id;
      }
      if (state.dateRange.start) params.dateDebut = state.dateRange.start;
      if (state.dateRange.end) params.dateFin = state.dateRange.end;
      if (state.activeTab === "unpaid") params.payment_status = "Non payé";
      if (state.activeTab === "partially_paid")
        params.payment_status = "Partiellement payé";
      if (state.activeTab === "paid") params.payment_status = "Payé";

      const response = await api.fetchVentes(params);
      if (requestId !== salesRequestRef.current) return;
      dispatch({
        type: "SET_VENTES",
        payload: {
          ventes: response.data,
          total: response.total,
          summary: response.summary,
        },
      });
    } catch (error) {
      if (requestId !== salesRequestRef.current) return;
      console.error("Erreur lors du chargement des ventes:", error);
      dispatch({
        type: "SET_ERROR",
        payload: "Erreur lors du chargement des ventes",
      });
    } finally {
      if (requestId === salesRequestRef.current) {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    }
  }, [
    state.currentPage,
    state.itemsPerPage,
    state.searchTerm,
    state.selectedClient,
    state.dateRange,
    state.showDeleted,
    state.activeTab,
    state.salesChannel,
    selectedWarehouse,
    selectedCompany,
    auth?.user?.id,
  ]);

  // useEffect to trigger refreshSales when filters change
  useEffect(() => {
    refreshSales();
    return () => {
      salesRequestRef.current += 1;
    };
  }, [refreshSales]);

  if (!auth) {
    return <Spin tip="Chargement de l'authentification..."></Spin>;
  }

  // Typons correctement l'objet auth pour éviter les erreurs TypeScript
  const typedAuth = auth as AuthContextType;

  // Vérifions si l'utilisateur est chargé
  if (!typedAuth.user) {
    return <Spin tip="Chargement des informations utilisateur..."></Spin>;
  }

  // Version débogage: afficher les permissions disponibles
  // console.debug("User permissions:", typedAuth.user.permissions);

  // Now safely check permissions after loading - sans utiliser @ts-ignore
  const canCreate = canCreateSaleDraft;
  // View permission is fundamental for the component
  const canView = canViewSaleDraft;
  // View deleted permission (using view for now)
  const canViewDeleted = canView; // Or potentially delete permission if required

  const openVenteForm = () => {
    // Permission check before opening form
    if (!canCreate) {
      message.error("Vous n'avez pas la permission de créer une vente.");
      return;
    }

    // Original logic for openVenteForm
    const warehouseId = selectedWarehouse?.id ?? selectedWarehouse;
    if (!warehouseId) {
      Modal.warning({
        title: "Sélection requise",
        content: "Veuillez sélectionner un établissement avant de créer une vente.",
      });
      return;
    }
    // Reset form data with current context
    dispatch({
      type: "SET_FORM_DATA",
      payload: {
        ...initialState.formData,
        Date_Facture: dayjs().format("YYYY-MM-DD"),
        Statut_Vente: "Completé",
        Client_ID: "", // Ensure client is reset
        produitsVendus: [],
        remise_globale: 0,
      },
    });
    dispatch({ type: "TOGGLE_FORM", payload: true });
  };

  const handleTabChange = (activeKey: string) => {
    // No permission check needed to change tabs
    dispatch({ type: "SET_ACTIVE_TAB", payload: activeKey });
  };

  const salesCount = state.totalRecords || state.salesSummary.total_sales_count;
  const hasActiveFilters = Boolean(
    state.searchTerm.trim() ||
      state.selectedClient ||
      state.salesChannel !== "all" ||
      state.dateRange.start ||
      state.dateRange.end ||
      state.showDeleted,
  );
  const salesPeriodLabel =
    state.dateRange.start && state.dateRange.end
      ? `${dayjs(state.dateRange.start).format("DD/MM/YYYY")} – ${dayjs(
          state.dateRange.end,
        ).format("DD/MM/YYYY")}`
      : state.dateRange.start
        ? `Depuis le ${dayjs(state.dateRange.start).format("DD/MM/YYYY")}`
        : state.dateRange.end
          ? `Jusqu’au ${dayjs(state.dateRange.end).format("DD/MM/YYYY")}`
          : "Toutes périodes";

  const resetSalesFilters = () => {
    dispatch({ type: "SET_SEARCH", payload: "" });
    dispatch({ type: "SET_SELECTED_CLIENT", payload: null });
    dispatch({ type: "SET_SALES_CHANNEL", payload: "all" });
    dispatch({ type: "SET_DATE_RANGE", payload: { start: "", end: "" } });
    if (state.showDeleted) {
      dispatch({ type: "TOGGLE_SHOW_DELETED", payload: false });
    }
  };

  const renderSalesTabLabel = (label: string, key: string) => (
    <span>
      {label}
      {state.activeTab === key && (
        <span className="sales-page__tab-count">
          ({formatNumber(salesCount)})
        </span>
      )}
    </span>
  );

  // If user doesn't even have view permission, show a message or redirect
  if (!canView) {
    return (
      <Layout>
        <Layout.Content style={{ padding: "24px", textAlign: "center" }}>
          <Card>
            <p>
              Vous n'avez pas la permission de visualiser le module de gestion
              des ventes.
            </p>
            {/* Optionally add a button to go back or contact admin */}
          </Card>
        </Layout.Content>
      </Layout>
    );
  }

  return (
    // Pass auth down through the context
    <VentesContext.Provider
      value={{
        state,
        dispatch,
        api,
        auth: typedAuth,
        restoredDraftMeta,
        clearRestoredSalesDraft,
      }}
    >
      <Layout className="sales-page-shell">
        <Layout.Content className="sales-page">
          <header className="sales-page__header">
            <div className="sales-page__heading">
              <h1 id="sales-page-title">Ventes</h1>
              <p>Suivre les ventes, les encaissements et les livraisons</p>
              <span className="sales-page__scope">
                {formatNumber(salesCount)} vente{salesCount === 1 ? "" : "s"}
                <span aria-hidden="true">•</span>
                {salesPeriodLabel}
              </span>
            </div>

            <div className="sales-page__actions">
              <span className="sales-page__audio-toggle">
                <BusinessAudioToggle
                  alertType="pos_cart_add"
                  label="Sons des ventes"
                />
              </span>
              {canCreate && (
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={openVenteForm}
                  className="sales-page__primary-action"
                >
                  Nouvelle vente
                </Button>
              )}
            </div>
          </header>

          {canView && <SalesSummaryPanel summary={state.salesSummary} />}

          {canView && (
            <section
              className="sales-page__filters"
              aria-label="Filtres des ventes"
            >
              <Input
                value={state.searchTerm}
                allowClear
                aria-label="Rechercher une vente"
                placeholder="Rechercher un numéro de facture ou un client"
                prefix={<SearchOutlined />}
                onChange={(e) =>
                  dispatch({ type: "SET_SEARCH", payload: e.target.value })
                }
                className="sales-page__search"
              />
              <Select
                placeholder="Tous les clients"
                aria-label="Filtrer les ventes par client"
                allowClear
                showSearch
                optionFilterProp="searchLabel"
                filterOption={(input, option: any) =>
                  matchesCommercialEntitySearch(option?.searchLabel, input)
                }
                value={state.selectedClient?.id ?? undefined}
                disabled={state.salesChannel === "ecommerce"}
                onChange={(value?: number) => {
                  const selectedClient = filteredClientsForFilter.find(
                    (client) => client.id === value,
                  );
                  dispatch({
                    type: "SET_SELECTED_CLIENT",
                    payload: selectedClient || null,
                  });
                }}
                className="sales-page__client-filter"
              >
                {filteredClientsForFilter.map((client) => (
                  <Option
                    key={client.id}
                    value={client.id}
                    searchLabel={buildCommercialEntitySearchText(client)}
                  >
                    {getCommercialEntityDisplayName(client, "Client inconnu")}
                  </Option>
                ))}
              </Select>
              <Select
                aria-label="Filtrer les ventes par canal"
                value={state.salesChannel}
                onChange={(value: SalesChannel) =>
                  dispatch({ type: "SET_SALES_CHANNEL", payload: value })
                }
                className="sales-page__channel-filter"
              >
                {SALES_CHANNEL_OPTIONS.map((channel) => (
                  <Option key={channel.value} value={channel.value}>
                    {channel.label}
                  </Option>
                ))}
              </Select>
              <RangePicker
                value={
                  state.dateRange.start && state.dateRange.end
                    ? [
                        dayjs(state.dateRange.start),
                        dayjs(state.dateRange.end),
                      ]
                    : null
                }
                format="DD/MM/YYYY"
                allowClear
                presets={getSalesDateRangePresets()}
                placeholder={["Début", "Fin"]}
                aria-label="Filtrer les ventes par période"
                onChange={(dates) =>
                  dispatch({
                    type: "SET_DATE_RANGE",
                    payload: {
                      start: dates?.[0]?.format("YYYY-MM-DD") || "",
                      end: dates?.[1]?.format("YYYY-MM-DD") || "",
                    },
                  })
                }
                className="sales-page__period-filter"
              />
              <Button
                type="text"
                icon={<ReloadOutlined />}
                disabled={!hasActiveFilters}
                onClick={resetSalesFilters}
                className="sales-page__reset"
              >
                Réinitialiser
              </Button>
              {canViewDeleted && (
                <Badge
                  count={state.showDeleted ? 1 : 0}
                  size="small"
                  color="var(--blue-elsa)"
                >
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: [
                        {
                          key: "deleted",
                          icon: <DeleteOutlined />,
                          label: state.showDeleted
                            ? "Masquer les ventes supprimées"
                            : "Afficher les ventes supprimées",
                        },
                      ],
                      onClick: ({ key }) => {
                        if (key === "deleted") {
                          dispatch({
                            type: "TOGGLE_SHOW_DELETED",
                            payload: !state.showDeleted,
                          });
                        }
                      },
                    }}
                  >
                    <Button
                      icon={<FilterOutlined />}
                      className={
                        state.showDeleted
                          ? "sales-page__more-filters is-active"
                          : "sales-page__more-filters"
                      }
                    >
                      Filtres
                    </Button>
                  </Dropdown>
                </Badge>
              )}
            </section>
          )}

          {state.error && (
            <Alert
              type="error"
              showIcon
              closable
              message={state.error}
              onClose={() => dispatch({ type: "SET_ERROR", payload: null })}
              className="sales-page__error"
            />
          )}

          {canView && (
            <Tabs
              activeKey={state.activeTab}
              onChange={handleTabChange}
              className="sales-page__tabs"
            >
              <Tabs.TabPane
                tab={renderSalesTabLabel("Toutes les ventes", "all")}
                key="all"
              >
                <VentesTable refreshSales={refreshSales} active={state.activeTab === "all"} />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab={renderSalesTabLabel("Non payées", "unpaid")}
                key="unpaid"
              >
                <VentesTable refreshSales={refreshSales} active={state.activeTab === "unpaid"} />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab={renderSalesTabLabel(
                  "Partiellement payées",
                  "partially_paid",
                )}
                key="partially_paid"
              >
                <VentesTable refreshSales={refreshSales} active={state.activeTab === "partially_paid"} />
              </Tabs.TabPane>
              <Tabs.TabPane
                tab={renderSalesTabLabel("Payées", "paid")}
                key="paid"
              >
                <VentesTable refreshSales={refreshSales} active={state.activeTab === "paid"} />
              </Tabs.TabPane>
            </Tabs>
          )}
        </Layout.Content>
      </Layout>
    </VentesContext.Provider>
  );
};

export default GestionVentes;
